import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ToggleButton } from "primereact/togglebutton";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { SHOW_ADL_ENTRY_POINTS } from "../../../constants/uiFlags";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import { ResidentProfile } from "../../../models/residentProfile";
import { getMedicationScheduleOccurrences, getTodayMedicationTimingMeta } from "../../resident/utils/emarScheduleUtils";
import { convertIsoToDate } from "../../../utils/stringUtils";
import Unauthorized from "../../../shared/pages/Unauthorized";
import AddHomeModal from "../../home/components/AddHomeModal";
import AddResidentModal from "../../home/components/AddResidentModal";
import ResidentEntryTrackerButton from "../../resident/components/ResidentEntryTrackerButton";
import EditHomeProfileModal from "../../home/components/EditHomeProfileModal";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import { appConfirmDialogClasses, renderConfirmMessage } from "../../../components/general/uiSurfaceStyles";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../../constants/brandConstants";
import { SHOW_RESIDENT_SCHEDULES } from "../../../constants/uiFlags";

const normalizeEmarBoardText = (value: any) => String(value || "").trim();
const FACILITY_ACTIVE_RESIDENT_LIMITS: Record<string, number> = {
  afh: 8,
  esf: 16,
};
const RESIDENT_TABLE_PAGE_SIZE = 10;
const getLocalIsoDateOnly = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getEntryRecordedLocalDateForBoard = (entry: any) => {
  const timestamp = normalizeEmarBoardText(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt);
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const filterEmarBoardEntriesForDate = (entries: any[], entryDate: string) => (
  (Array.isArray(entries) ? entries : []).filter((entry: any) => {
    const recordedLocalDate = getEntryRecordedLocalDateForBoard(entry);
    return !recordedLocalDate || recordedLocalDate === entryDate;
  })
);

const normalizeEmarBoardScheduledTimeKey = (value: any, status?: string) => {
  const normalizedValue = normalizeEmarBoardText(value).toLowerCase();
  if (normalizedValue) {
    return normalizedValue;
  }
  return normalizeEmarBoardText(status).toLowerCase() === "prn_given" ? "prn" : "default";
};

const hasEmarBoardCorrectionEvent = (entry: any) => (
  Array.isArray(entry?.statusHistory)
  && entry.statusHistory.some((event: any) => normalizeEmarBoardText(event?.eventType).toLowerCase() === "entry_corrected")
);

const hasCompletedEmarBoardPrnFollowUp = (entry: any) => (
  normalizeEmarBoardText(entry?.status).toLowerCase() === "prn_given"
  && !!normalizeEmarBoardText(entry?.prnReassessedAt)
  && !!normalizeEmarBoardText(entry?.prnEffectiveness)
  && !!normalizeEmarBoardText(entry?.prnSideEffectCode)
);

const parseEmarBoardExpiryValue = (value: any) => {
  const normalized = normalizeEmarBoardText(value);
  if (!normalized) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const parsed = new Date(`${normalized}T23:59:59`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{2})$/);
  if (yearMonthMatch) {
    const yearValue = Number(yearMonthMatch[1]);
    const monthIndex = Number(yearMonthMatch[2]) - 1;
    if (monthIndex >= 0 && monthIndex <= 11) {
      return new Date(yearValue, monthIndex + 1, 0, 23, 59, 59, 999);
    }
  }
  const monthYearMatch = normalized.match(/^(\d{1,2})\/(\d{4})$/);
  if (monthYearMatch) {
    const monthIndex = Number(monthYearMatch[1]) - 1;
    const yearValue = Number(monthYearMatch[2]);
    if (monthIndex >= 0 && monthIndex <= 11) {
      return new Date(yearValue, monthIndex + 1, 0, 23, 59, 59, 999);
    }
  }
  return null;
};

const isEmarBoardMedicationExpiringSoon = (value: any) => {
  const parsedExpiry = parseEmarBoardExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  const now = new Date();
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + 30);
  return parsedExpiry.getTime() >= now.getTime() && parsedExpiry.getTime() <= threshold.getTime();
};
const isEmarBoardMedicationExpired = (value: any) => {
  const parsedExpiry = parseEmarBoardExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  return parsedExpiry.getTime() < Date.now();
};

const residentToolBadgeStyle = (minWidth?: string): React.CSSProperties => ({
  ...(minWidth ? { minWidth } : {}),
  padding: "0.2rem 0.6rem",
  fontSize: "0.74rem",
  fontWeight: 700,
  lineHeight: 1.2,
  minHeight: 0,
  whiteSpace: "nowrap",
});

const buildEmarBoardResidentStatus = (residentId: string, medications: any[], entries: any[], dailyTrackingItems: any[] = []) => {
  const activeMedications = (Array.isArray(medications) ? medications : []).filter((item: any) => item?.active !== false);
  const activeTrackingItems = (Array.isArray(dailyTrackingItems) ? dailyTrackingItems : []).filter((item: any) => item?.active !== false && item?.discontinued !== true);
  const nonExpiredActiveMedications = activeMedications.filter((item: any) => !isEmarBoardMedicationExpired(item?.effectiveEndDate));
  const scheduledActiveMedications = nonExpiredActiveMedications.filter((item: any) => item?.prn !== true);
  const unscheduledNonPrnCount = nonExpiredActiveMedications.filter((item: any) => item?.prn !== true && getMedicationScheduleOccurrences(item).some((occurrence: any) => String(occurrence?.scheduledTimeKey || "") === "default")).length;
  const expiredCount = activeMedications.filter((item: any) => isEmarBoardMedicationExpired(item?.effectiveEndDate)).length;
  const expiringSoonCount = nonExpiredActiveMedications.filter((item: any) => isEmarBoardMedicationExpiringSoon(item?.effectiveEndDate)).length;
  const filteredEntries = Array.isArray(entries) ? entries : [];
  const scheduledEntriesByOccurrenceKey = new Set(
    filteredEntries
      .filter((item: any) => normalizeEmarBoardText(item?.status).toLowerCase() !== "prn_given")
      .map((item: any) => {
        const medicationId = normalizeEmarBoardText(item?.medicationId);
        const scheduledTimeKey = normalizeEmarBoardScheduledTimeKey(item?.scheduledTimeKey, item?.status);
        return medicationId ? `${medicationId}::${scheduledTimeKey}` : "";
      })
      .filter(Boolean),
  );
  const medicationOccurrences = scheduledActiveMedications.flatMap((item: any) => {
    const medicationId = normalizeEmarBoardText(item?.medicationId);
    if (!medicationId) {
      return [];
    }
    return getMedicationScheduleOccurrences(item).map((occurrence) => ({
      medicationId,
      scheduledTimeKey: normalizeEmarBoardScheduledTimeKey(
        occurrence?.scheduledTimeKey,
        item?.prn ? "prn_given" : "",
      ),
      scheduledClockTime: normalizeEmarBoardText(occurrence?.scheduledClockTime),
      scheduleText: normalizeEmarBoardText(occurrence?.scheduleText || item?.schedule),
    }));
  });
  const nonPrnEntries = filteredEntries.filter((item: any) => normalizeEmarBoardText(item?.status).toLowerCase() !== "prn_given");
  const completedCount = filteredEntries.filter((item: any) => normalizeEmarBoardText(item?.status).toLowerCase() === "given").length;
  const missedCount = filteredEntries.filter((item: any) => normalizeEmarBoardText(item?.status).toLowerCase() === "missed").length;
  const exceptionCount = filteredEntries.filter((item: any) => ["refused", "held", "not_given", "missed"].includes(normalizeEmarBoardText(item?.status).toLowerCase())).length;
  const unreviewedExceptionCount = filteredEntries.filter((item: any) => {
    const status = normalizeEmarBoardText(item?.status).toLowerCase();
    return ["refused", "held", "not_given", "missed"].includes(status) && item?.exceptionResolved !== true && item?.exceptionReviewed !== true;
  }).length;
  const prnReviewCount = filteredEntries.filter((item: any) =>
    normalizeEmarBoardText(item?.status).toLowerCase() === "prn_given"
    && item?.exceptionResolved !== true
    && !hasCompletedEmarBoardPrnFollowUp(item)).length;
  const correctedEntryCount = filteredEntries.filter((item: any) =>
    item?.exceptionResolved !== true
    && hasEmarBoardCorrectionEvent(item)).length;
  const scheduledPendingCount = Math.max(medicationOccurrences.length - nonPrnEntries.length, 0);
  const pendingCount = scheduledPendingCount + prnReviewCount;
  const dueNowCount = medicationOccurrences.filter((occurrence) => {
    const occurrenceKey = `${occurrence.medicationId}::${occurrence.scheduledTimeKey}`;
    if (scheduledEntriesByOccurrenceKey.has(occurrenceKey)) {
      return false;
    }
    return getTodayMedicationTimingMeta({
      schedule: occurrence.scheduleText,
      scheduledClockTime: occurrence.scheduledClockTime,
    }, false).isDueSoon;
  }).length;
  const overdueCount = medicationOccurrences.filter((occurrence) => {
    const occurrenceKey = `${occurrence.medicationId}::${occurrence.scheduledTimeKey}`;
    if (scheduledEntriesByOccurrenceKey.has(occurrenceKey)) {
      return false;
    }
    return getTodayMedicationTimingMeta({
      schedule: occurrence.scheduleText,
      scheduledClockTime: occurrence.scheduledClockTime,
    }, false).isOverdue;
  }).length;

  const attentionBadges = [
    expiredCount ? { label: `${expiredCount} expired`, severity: "danger" } : null,
    overdueCount ? { label: `${overdueCount} overdue`, severity: "danger" } : null,
    missedCount ? { label: `${missedCount} missed`, severity: "danger" } : null,
    unscheduledNonPrnCount ? { label: `${unscheduledNonPrnCount} unscheduled`, severity: "warning" } : null,
    unreviewedExceptionCount ? { label: `${unreviewedExceptionCount} unreviewed`, severity: "warning" } : null,
    prnReviewCount ? { label: `${prnReviewCount} PRN review`, severity: "info" } : null,
    correctedEntryCount ? { label: `${correctedEntryCount} Corrected entry`, severity: "warning" } : null,
    expiringSoonCount ? { label: `${expiringSoonCount} expiring soon`, severity: "danger" } : null,
  ].filter(Boolean);
  const needsFollowUp = exceptionCount > 0
    || overdueCount > 0
    || unreviewedExceptionCount > 0
    || prnReviewCount > 0
    || correctedEntryCount > 0
    || expiredCount > 0
    || expiringSoonCount > 0;

  let statusLabel = "Pending";
  let severity: "secondary" | "warning" | "success" | "danger" = "warning";
  if (!activeMedications.length) {
    statusLabel = "Setup Needed";
    severity = "secondary";
  } else if (needsFollowUp) {
    statusLabel = "Needs Attention";
    severity = "danger";
  } else if (pendingCount === 0) {
    statusLabel = "Complete";
    severity = "success";
  } else if (completedCount > 0) {
    statusLabel = "In Progress";
    severity = "warning";
  }

  const summaryDesktop = !activeMedications.length
    ? "No active meds"
    : `${completedCount || 0} complete - ${pendingCount || 0} pending${missedCount ? ` - ${missedCount} missed` : ""}${expiredCount ? ` - ${expiredCount} expired` : ""}${unscheduledNonPrnCount ? ` - ${unscheduledNonPrnCount} unscheduled` : ""}${overdueCount ? ` - ${overdueCount} overdue` : ""}${exceptionCount ? ` - ${exceptionCount} attention` : ""}${expiringSoonCount ? ` - ${expiringSoonCount} expiring soon` : ""}${activeTrackingItems.length ? ` - ${activeTrackingItems.length} tracking items` : ""}`;
  const summaryMobile = !activeMedications.length
    ? "No active meds set up yet."
    : `${completedCount || 0} done${pendingCount ? `, ${pendingCount} pending` : ""}${missedCount ? `, ${missedCount} missed` : ""}${expiredCount ? `, ${expiredCount} expired` : ""}${unscheduledNonPrnCount ? `, ${unscheduledNonPrnCount} unscheduled` : ""}${overdueCount ? `, ${overdueCount} overdue` : ""}${prnReviewCount ? `, ${prnReviewCount} PRN review` : ""}${correctedEntryCount ? `, ${correctedEntryCount} corrected` : ""}${expiringSoonCount ? `, ${expiringSoonCount} expiring soon` : ""}${activeTrackingItems.length ? `, ${activeTrackingItems.length} tracking items` : ""}.`;

  return {
    residentId,
    activeMedicationCount: activeMedications.length,
    entryCount: filteredEntries.length,
    completedCount,
    missedCount,
    exceptionCount,
    unreviewedExceptionCount,
    prnReviewCount,
    correctedEntryCount,
    unscheduledNonPrnCount,
    expiredCount,
    expiringSoonCount,
    dailyTrackingCount: activeTrackingItems.length,
    dueNowCount,
    overdueCount,
    pendingCount,
    attentionBadges,
    statusLabel,
    severity,
    summaryDesktop,
    summaryMobile,
  };
};

const DashboardProvider = () => {
  const toast = React.useRef<Toast>(null);
  const mobile = useResponsive().isMobile;
  const navigate = useNavigate();
  const location = useLocation();
  const { completeStep, isStepActive } = useTour();
  const authExperience = useAuthExperience();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const [businessHomes, setBusinessHomes] = useState<BusinessHomeProfile[]>([]);
  const [homesLoaded, setHomesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openResidentModal, setOpenResidentModal] = useState(false);
  const [openEditHomeModal, setOpenEditHomeModal] = useState(false);
  const [addFlag, setAddFlag] = useState(false);
  const [editHomeData, setEditHomeData] = useState<BusinessHomeProfile | null>(null);
  const [editHomeLoading, setEditHomeLoading] = useState(false);
  const [selectedHomeIdForResident, setSelectedHomeIdForResident] = useState<string>();
  const [selectedHomeId, setSelectedHomeId] = useState<string>("");
  const [initialHomeAutoSelectionApplied, setInitialHomeAutoSelectionApplied] = useState(false);
  const [residentsByHome, setResidentsByHome] = useState<Record<string, ResidentProfile[]>>({});
  const [loadingResidents, setLoadingResidents] = useState(false);
  const [residentPromptHandledForEmptyState, setResidentPromptHandledForEmptyState] = useState(false);
  const [showArchivedResidents, setShowArchivedResidents] = useState(false);
  const [adminResidentSearch, setAdminResidentSearch] = useState("");
  const [adminResidentSortField, setAdminResidentSortField] = useState("residentName");
  const [adminResidentSortOrder, setAdminResidentSortOrder] = useState<1 | -1>(1);
  const [residentTableFirst, setResidentTableFirst] = useState(0);
  const [linkedCaregiversByHome, setLinkedCaregiversByHome] = useState<Record<string, any[]>>({});
  const [caregiverSharesByHome, setCaregiverSharesByHome] = useState<Record<string, any[]>>({});
  const [loadingCaregivers, setLoadingCaregivers] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [selectedCaregiverForSharing, setSelectedCaregiverForSharing] = useState<any | null>(null);
  const [selectedCaregiverForPermissions, setSelectedCaregiverForPermissions] = useState<string>("");
  const [selectedSharedResidentIds, setSelectedSharedResidentIds] = useState<string[]>([]);
  const [carePlanAccessHintVisible, setCarePlanAccessHintVisible] = useState(false);
  const [savingCaregiverShares, setSavingCaregiverShares] = useState(false);
  const [emarBoardLoading, setEmarBoardLoading] = useState(false);
  const [emarBoardByResidentId, setEmarBoardByResidentId] = useState<Record<string, any>>({});
  const [emarBoardFilter, setEmarBoardFilter] = useState<"all" | "attention" | "due_now" | "complete" | "setup">("all");
  const [adminEmarBoardSearch, setAdminEmarBoardSearch] = useState("");
  const [adminEmarBoardSortField, setAdminEmarBoardSortField] = useState("residentName");
  const [adminEmarBoardSortOrder, setAdminEmarBoardSortOrder] = useState<1 | -1>(1);
  const [shiftDialogVisible, setShiftDialogVisible] = useState(false);
  const [selectedCaregiverForShift, setSelectedCaregiverForShift] = useState<any | null>(null);
  const [shiftEnabled, setShiftEnabled] = useState(false);
  const [shiftDays, setShiftDays] = useState<string[]>([]);
  const [shiftStartTime, setShiftStartTime] = useState("08:00");
  const [shiftEndTime, setShiftEndTime] = useState("16:00");
  const [mobileAccessEnabled, setMobileAccessEnabled] = useState(true);
  const [savingCaregiverShift, setSavingCaregiverShift] = useState(false);
  const [allDayShiftAccess, setAllDayShiftAccess] = useState(false);
  const [isProviderSubscribed, setIsProviderSubscribed] = useState(true);
  const [accessUnlockActive, setAccessUnlockActive] = useState(false);
  const [accessUnlockExpiresAt, setAccessUnlockExpiresAt] = useState<string | null>(null);
  const [providerAllowedCaregivers, setProviderAllowedCaregivers] = useState(0);
  const [providerAllowedResidents, setProviderAllowedResidents] = useState(0);
  const [providerNurseResidentIds, setProviderNurseResidentIds] = useState<string[]>([]);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showResidentSlotLimitDialog, setShowResidentSlotLimitDialog] = useState(false);
  const [showResidentArchiveLimitDialog, setShowResidentArchiveLimitDialog] = useState(false);
  const [residentArchiveLimitDetails, setResidentArchiveLimitDetails] = useState<{ billingPeriodStart?: string | null; billingPeriodEnd?: string | null }>({});
  const [subscriptionFeatureName, setSubscriptionFeatureName] = useState("this feature");
  const [subscriptionFeatureDetails, setSubscriptionFeatureDetails] = useState<string[]>([]);
  const [residentAiDialogVisible, setResidentAiDialogVisible] = useState(false);
  const [residentAiDialogContext, setResidentAiDialogContext] = useState<{
    residentId: string;
    homeId: string;
    residentName: string;
  } | null>(null);
  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const facilitySingularLabel = "Home";
  const facilitySingularLabelLower = facilitySingularLabel.toLowerCase();
  const facilityPluralLabel = "Homes";
  const actorLabel = "Provider";
  const actorLabelLower = actorLabel.toLowerCase();
  const isOneHubForCareBrand = authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE;
  const addFacilityLabel = "Add Home";
  const manageFacilityLabel = `Manage ${facilitySingularLabel}`;
  const normalizedPathname = String(location.pathname || "").toLowerCase();
  const shareOptionHintConfig: Record<"carePlan" | "residentShare" | "behaviorLog" | "incidentReports" | "dailyNotes" | "emar" | "adl", { title: string; details: string[] }> = {
    residentShare: {
      title: "Resident Share",
      details: [
        "Caregiver can see this resident in their dashboard.",
        "They can open shared resident tools you allow for this resident.",
      ],
    },
    carePlan: {
      title: "Search Care Plan",
      details: [
        "Caregiver can open the resident care plan search view.",
        "They can see care plan sections, descriptions, and instructions.",
        "They cannot edit care plan data.",
      ],
    },
    behaviorLog: {
      title: "Daily Behavior Log",
      details: [
        "Caregiver can open the resident daily behavior log area.",
        "They can add new daily behavior entries and update their own entries.",
        `They cannot generate ${actorLabelLower} reports.`,
      ],
    },
    incidentReports: {
      title: "Incident Reports",
      details: [
        "Caregiver can open the resident incident reports area.",
        "They can add incident reports and manage their own allowed entries.",
        `${actorLabel} keeps full review access.`,
      ],
    },
    dailyNotes: {
      title: "Progress Notes",
      details: [
        "Caregiver can open the resident daily notes area.",
        "They can add shift notes and update their own allowed entries.",
        `${actorLabel} keeps full review access.`,
      ],
    },
    emar: {
      title: "eMAR",
      details: [
        "Caregiver can open the resident eMAR area.",
        "They can chart medication passes and PRN follow-up for shared residents.",
        `${actorLabel} keeps full review and override access.`,
      ],
    },
    adl: {
      title: "ADL Tracking",
      details: [
        "Caregiver can open the resident ADL tracking page.",
        "They can chart published ADL tasks and notes for shared residents.",
        `${actorLabel} keeps full setup, publish, and review access.`,
      ],
    },
  };
  const isActiveResident = (resident: any) => {
    const activeValue = resident?.active;
    if (typeof activeValue === "string") {
      return activeValue.trim().toLowerCase() !== "false";
    }
    return activeValue !== false && activeValue !== 0;
  };
  const linkedCaregiverCountAcrossHomes = Object.values(linkedCaregiversByHome).reduce(
    (sum, caregivers) => sum + (Array.isArray(caregivers) ? caregivers.length : 0),
    0,
  );
  const remainingProviderCaregiverSlots = isAdminUser
    ? Number.MAX_SAFE_INTEGER
    : Math.max(0, Number(providerAllowedCaregivers || 0) - linkedCaregiverCountAcrossHomes);
  const activeResidentCountAcrossHomes = useMemo(
    () => Object.values(residentsByHome).reduce(
      (sum, residents) => sum + (Array.isArray(residents) ? residents.filter((resident) => isActiveResident(resident)).length : 0),
      0,
    ),
    [residentsByHome],
  );
  const activeResidentIdsAcrossHomes = useMemo(
    () => new Set(
      Object.values(residentsByHome)
        .flatMap((residents) => (Array.isArray(residents) ? residents : []))
        .filter((resident) => isActiveResident(resident))
        .map((resident) => String(resident?.residentId || "").trim())
        .filter(Boolean),
    ),
    [residentsByHome],
  );
  const providerNurseResidentCountAcrossHomes = useMemo(
    () => Array.from(new Set(
      (Array.isArray(providerNurseResidentIds) ? providerNurseResidentIds : [])
        .map((residentId) => String(residentId || "").trim())
        .filter((residentId) => !!residentId && !activeResidentIdsAcrossHomes.has(residentId)),
    )).length,
    [activeResidentIdsAcrossHomes, providerNurseResidentIds],
  );
  const totalActiveResidentCountAcrossHomes = activeResidentCountAcrossHomes + providerNurseResidentCountAcrossHomes;
  const canAddResidentAcrossProviderWorkspaces = isAdminUser
    || Number(providerAllowedResidents || 0) <= 0
    || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);
  const shiftDayOptions = [
    { key: "mon", label: "Mon" },
    { key: "tue", label: "Tue" },
    { key: "wed", label: "Wed" },
    { key: "thu", label: "Thu" },
    { key: "fri", label: "Fri" },
    { key: "sat", label: "Sat" },
    { key: "sun", label: "Sun" },
  ];
  const halfHourTimeOptions = Array.from({ length: 48 }, (_, index) => {
    const hour = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? "00" : "30";
    const value = `${String(hour).padStart(2, "0")}:${minutes}`;
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    const meridiem = hour < 12 ? "AM" : "PM";
    return {
      value,
      label: `${hour12}:${minutes} ${meridiem}`,
    };
  });
  const formatShiftSummary = (caregiver: any) => {
    const shiftWindows = Array.isArray(caregiver?.shiftWindows) ? caregiver.shiftWindows : [];
    if (shiftWindows.length) {
      const groupedWindows = shiftWindows.reduce((acc: Record<string, string[]>, window: any) => {
        const key = `${String(window?.startTime || "").trim()}-${String(window?.endTime || "").trim()}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(String(window?.day || "").trim().toLowerCase());
        return acc;
      }, {});
      return Object.entries(groupedWindows)
        .map(([timeRange, days]) => {
          const normalizedDays = Array.isArray(days) ? days : [];
          const orderedLabels = shiftDayOptions
            .filter((option) => normalizedDays.includes(option.key))
            .map((option) => option.label);
          return `${orderedLabels.join(", ")} · ${String(timeRange || "").replace("-", " - ")}`;
        })
        .join(" | ");
    }
    const days = Array.isArray(caregiver?.shiftDays) ? caregiver.shiftDays : [];
    const startTime = String(caregiver?.shiftStartTime || "").trim();
    const endTime = String(caregiver?.shiftEndTime || "").trim();
    if (!caregiver?.shiftEnabled || !days.length || !startTime || !endTime) {
      return "No shift set";
    }
    return `${days.map((day: string) => day.slice(0, 1).toUpperCase() + day.slice(1)).join(", ")} · ${startTime} - ${endTime}`;
  };
  const openResidentAiDialog = (resident: ResidentProfile, homeId: string) => {
    const residentId = String(resident?.residentId || "").trim();
    if (!residentId) {
      return;
    }
    setResidentAiDialogContext({
      residentId,
      homeId: String(homeId || "").trim(),
      residentName: `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident",
    });
    setResidentAiDialogVisible(true);
  };
  const closeResidentAiDialog = () => {
    setResidentAiDialogVisible(false);
    setResidentAiDialogContext(null);
  };
  const openProfileSetupDialog = (resident: ResidentProfile, homeId: string) => {
    const residentId = String(resident?.residentId || "").trim();
    if (!residentId) {
      return;
    }
    navigate(getResidentCarePlanPath(String(homeId || "").trim(), residentId), {
      state: { openAssessmentToNcp: true, profileSetup: true },
    });
  };
  const openResidentAiTool = (tool: "summarize-assessment" | "compare-assessments" | "compare-care-plans" | "compare-mar-sheet") => {
    if (!residentAiDialogContext) {
      return;
    }
    runSubscribedProviderAction("Helpful AI Tools", () => {
      navigate(
        RouterAddress.getResidentDetailsPagePath(
          residentAiDialogContext.homeId,
          residentAiDialogContext.residentId,
          "ai-tools",
          tool,
        ),
      );
      closeResidentAiDialog();
    });
  };
  const handleHomeAddFlagChange = (flag: boolean) => {
    setHomesLoaded(false);
    setAddFlag(flag);
  };

  const providerSubscriptionFeatureDetails: Record<string, string[]> = {
    "Search Care Plan": [
      "View care plan sections, summaries, and care instructions.",
      "Quickly find resident guidance during a shift.",
    ],
    "Behavior Tracker": [
      "Track behaviors, observations, and follow-up notes.",
      "Keep a resident behavior history that can be reviewed later.",
      "Generate monthly behavior reports for review and follow-up.",
    ],
    "eMAR": [
      "Add or import medications from the care plan or MAR sheet, including dose, route, and schedule.",
      "Record actions such as Given, Refused, Held, or Not Given, with notes and PRN follow-up.",
      "Use Past Entries to review or correct charting, then export medication reports.",
    ],
    "ADL Tracking": [
      "Create tasks directly from the care plan, such as bathing, dressing, or mobility support.",
      "Record actions such as Done, Refused, Family, or Hospitalized, with support details and notes.",
      "Use Past Entries to review or correct prior charting, then export daily or monthly reports.",
    ],
    "Incident Reports": [
      "Document incidents with resident-specific details.",
      "Keep reports organized for provider review and follow-up.",
    ],
    "Daily Notes": [
      "Capture shift notes, updates, and handoff details.",
      "Help the team stay aligned on day-to-day resident care.",
    ],
    "Add Caregivers": [
      `Invite caregivers and link them to a ${facilitySingularLabelLower}.`,
      "Share selected residents and control caregiver access.",
      "Let caregivers open Search Care Plan, Behavior Tracker, Incident Reports, and Progress Notes for the residents you assign.",
      "Set caregiver shifts and manage which residents they can work with.",
    ],
  };

  const openSubscriptionDialog = (featureName: string) => {
    setSubscriptionFeatureName(featureName);
    setSubscriptionFeatureDetails(providerSubscriptionFeatureDetails[featureName] || []);
    setShowSubscriptionDialog(true);
  };

  const runSubscribedProviderAction = (featureName: string, action: () => void) => {
    if (isAdminUser) {
      action();
      return;
    }
    if (!isProviderSubscribed) {
      openSubscriptionDialog(featureName);
      return;
    }
    action();
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setHomesLoaded(false);
      setInitialHomeAutoSelectionApplied(false);
      try {
        await backendAPIStore.init();
        const [response, subscriptionDetails, nurseProvidersResult] = await Promise.allSettled([
          backendAPIStore.getHomeListForProvider(),
          backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER),
          backendAPIStore.getNurseProviders(),
        ]);
        if (response.status !== "fulfilled") {
          throw response.reason;
        }
        if (subscriptionDetails.status !== "fulfilled") {
          throw subscriptionDetails.reason;
        }

        setIsProviderSubscribed(isAdminUser || !!subscriptionDetails.value?.isSubscribed);
        setAccessUnlockActive(!!subscriptionDetails.value?.accessUnlockActive);
        setAccessUnlockExpiresAt(String(subscriptionDetails.value?.accessUnlockExpiresAt || "").trim() || null);
        setProviderAllowedCaregivers(Number(subscriptionDetails.value?.allowedCaregivers || 0));
        setProviderAllowedResidents(Number(subscriptionDetails.value?.allowedResidents || 0));
        setBusinessHomes(response.value.homes);
        if (nurseProvidersResult.status === "fulfilled") {
          const nurseResidentIds = (Array.isArray(nurseProvidersResult.value) ? nurseProvidersResult.value : [])
            .flatMap((provider: any) => (Array.isArray(provider?.residents) ? provider.residents : []))
            .filter((resident: any) => resident?.active !== false)
            .map((resident: any) => String(resident?.residentId || "").trim())
            .filter(Boolean);
          setProviderNurseResidentIds(nurseResidentIds);
        } else {
          setProviderNurseResidentIds([]);
        }
        setHomesLoaded(true);
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
        setAccessUnlockActive(false);
        setAccessUnlockExpiresAt(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [addFlag, authStore, backendAPIStore, userRoleType, isAdminUser]);

  const firstHome = businessHomes?.[0] as BusinessHomeProfile | undefined;
  const firstHomeId = firstHome?.homeId;
  const hasLoadedResidentsForAllHomes = useMemo(
    () => (businessHomes || []).every((home: BusinessHomeProfile) => !home?.homeId || (home.homeId in residentsByHome)),
    [businessHomes, residentsByHome],
  );
  const totalResidentsCount = useMemo(
    () => Object.values(residentsByHome).reduce((sum, residents) => sum + (Array.isArray(residents) ? residents.length : 0), 0),
    [residentsByHome],
  );
  const accessUnlockDaysLeft = useMemo(() => {
    if (!accessUnlockActive || !accessUnlockExpiresAt) {
      return 0;
    }
    const expiresAtMs = Date.parse(accessUnlockExpiresAt);
    if (!Number.isFinite(expiresAtMs)) {
      return 0;
    }
    const diffMs = expiresAtMs - Date.now();
    if (diffMs <= 0) {
      return 0;
    }
    return Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }, [accessUnlockActive, accessUnlockExpiresAt]);
  const formattedAccessUnlockEndDate = useMemo(() => {
    if (!accessUnlockExpiresAt) {
      return "";
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(accessUnlockExpiresAt));
  }, [accessUnlockExpiresAt]);

  useEffect(() => {
    if (!businessHomes?.length) {
      setSelectedHomeId("");
      setInitialHomeAutoSelectionApplied(false);
      return;
    }

    const hasSelectedHome = businessHomes.some((home: BusinessHomeProfile) => home.homeId === selectedHomeId);
    if (!hasSelectedHome) {
      setSelectedHomeId(businessHomes[0].homeId || "");
    }
  }, [businessHomes, selectedHomeId]);

  useEffect(() => {
    const homeCount = businessHomes?.length || 0;

    if (!homesLoaded || loading || !hasLoadedResidentsForAllHomes || initialHomeAutoSelectionApplied) {
      return;
    }

    if (homeCount <= 1) {
      setInitialHomeAutoSelectionApplied(true);
      return;
    }

    const normalizedFirstHomeId = String(firstHomeId || "").trim();
    const normalizedSelectedHomeId = String(selectedHomeId || "").trim();
    const selectedHomeIsDefault = !normalizedSelectedHomeId || normalizedSelectedHomeId === normalizedFirstHomeId;

    if (!selectedHomeIsDefault) {
      setInitialHomeAutoSelectionApplied(true);
      return;
    }

    const preferredHome = (businessHomes as BusinessHomeProfile[]).find((home) => {
      const homeId = String(home?.homeId || "").trim();
      return homeId && Array.isArray(residentsByHome[homeId]) && residentsByHome[homeId].some((resident) => resident?.active !== false);
    });

    if (preferredHome?.homeId && String(preferredHome.homeId).trim() !== normalizedSelectedHomeId) {
      setSelectedHomeId(preferredHome.homeId);
    }

    setInitialHomeAutoSelectionApplied(true);
  }, [
    businessHomes,
    firstHomeId,
    hasLoadedResidentsForAllHomes,
    homesLoaded,
    initialHomeAutoSelectionApplied,
    loading,
    residentsByHome,
    selectedHomeId,
  ]);

  useEffect(() => {
    const homeCount = businessHomes?.length || 0;

    if (!loading && homesLoaded && homeCount === 0) {
      setResidentPromptHandledForEmptyState(false);
      return;
    }

    if (!loading && homesLoaded && homeCount !== 1) {
      setResidentPromptHandledForEmptyState(false);
      return;
    }

    if (
      loading
      || loadingResidents
      || !homesLoaded
      || !firstHomeId
      || !(firstHomeId in residentsByHome)
      || (Array.isArray(residentsByHome[firstHomeId]) && residentsByHome[firstHomeId].some((resident) => resident?.active !== false))
      || residentPromptHandledForEmptyState
    ) {
      return;
    }

    setSelectedHomeId(firstHomeId);
    setResidentPromptHandledForEmptyState(true);
  }, [
    loading,
    loadingResidents,
    homesLoaded,
    businessHomes,
    firstHomeId,
    residentsByHome,
    residentPromptHandledForEmptyState,
  ]);

  const selectedHome = useMemo(
    () => (businessHomes as BusinessHomeProfile[]).find((home) => home.homeId === selectedHomeId) || null,
    [businessHomes, selectedHomeId],
  );
  const getResidentCarePlanPath = (targetHomeId: string, targetResidentId: string) => {
    const targetHome = (businessHomes as BusinessHomeProfile[]).find(
      (home) => String(home?.homeId || "").trim() === String(targetHomeId || "").trim(),
    );
    return RouterAddress.getResidentCarePlanPagePathForFacility(targetHomeId, targetResidentId, targetHome?.facilityType);
  };
  const hasResidentCarePlan = (resident: ResidentProfile | undefined | null) => resident?.hasCarePlan === true;
  const renderSetupRequiredBadge = (resident: ResidentProfile, homeId: string) => (
    isActiveResident(resident) && !hasResidentCarePlan(resident) ? (
      <Button
        type="button"
        label="Profile Set-up Required"
        icon="pi pi-exclamation-circle"
        severity="danger"
        rounded
        outlined
        size="small"
        style={{
          color: "#b91c1c",
          background: "#fff7f7",
          borderColor: "#fecaca",
          fontWeight: 700,
          padding: "0.2rem 0.6rem",
          fontSize: "0.74rem",
          lineHeight: 1.2,
          minHeight: "1.75rem",
          whiteSpace: "nowrap",
        }}
        onClick={() => openProfileSetupDialog(resident, homeId)}
      />
    ) : null
  );
  const selectedHomeFacilityType = String(selectedHome?.facilityType || "").trim().toLowerCase();
  const selectedHomeIsAfh = !selectedHomeFacilityType || selectedHomeFacilityType === "afh";
  const selectedHomeManageItems = useMemo(() => {
    if (!selectedHome?.homeId) {
      return [];
    }

    const items: any[] = [
      {
        label: "Edit Home Profile",
        icon: "pi pi-user-edit",
        command: () => {
          if (selectedHome.homeId === firstHomeId && (isStepActive("website-open-home") || isStepActive("assessment-tools-open-home"))) {
            completeStep(isStepActive("website-open-home") ? "website-open-home" : "assessment-tools-open-home");
          }
          void openEditHomeDialog(selectedHome.homeId);
        },
      },
    ];

    items.push({
      label: "Care Activity",
      icon: "pi pi-list-check",
      command: () => navigate(RouterAddress.getHomeCareActivityPagePath(selectedHome.homeId!)),
    });

    items.push({
      label: "Facility Tasks",
      icon: "pi pi-check-square",
      command: () => navigate(RouterAddress.getHomeFacilityTasksPagePath(selectedHome.homeId!)),
    });

    if (authExperience.featureFlags.providerTools.residentScreening) {
      items.push({
        label: "Resident Screening",
        icon: "pi pi-inbox",
        command: () => navigate(RouterAddress.getHomeIntakeReceivedPagePath(selectedHome.homeId!)),
      });
    }

    if (selectedHomeIsAfh) {
      items.push({
        label: "Website",
        icon: "pi pi-globe",
        command: () => navigate(RouterAddress.getManageWebsitePath(selectedHome.homeId!)),
      });
    }

    if (isAdminUser) {
      items.push({
        label: `Delete ${facilitySingularLabel}`,
        icon: "pi pi-trash",
        command: () => deleteSelectedHome(selectedHome.homeId),
      });
    }

    return items.sort((left, right) => String(left.label || "").localeCompare(String(right.label || "")));
  }, [
    completeStep,
    facilitySingularLabel,
    firstHomeId,
    isAdminUser,
    isStepActive,
    navigate,
    selectedHome,
    selectedHomeIsAfh,
  ]);

  const refreshCaregiverAccess = async (homeId?: string) => {
    const normalizedHomeId = String(homeId || "").trim();
    if (!normalizedHomeId) {
      return;
    }

    setLoadingCaregivers(true);
    try {
      await backendAPIStore.init();
      const [linkedCaregivers, caregiverShares] = await Promise.all([
        backendAPIStore.getLinkedCaregiversForHome(normalizedHomeId),
        backendAPIStore.getCaregiverResidentSharesForHome(normalizedHomeId),
      ]);

      setLinkedCaregiversByHome((prev) => ({
        ...prev,
        [normalizedHomeId]: Array.isArray(linkedCaregivers) ? linkedCaregivers : [],
      }));
      setCaregiverSharesByHome((prev) => ({
        ...prev,
        [normalizedHomeId]: Array.isArray(caregiverShares) ? caregiverShares.filter((share: any) => share?.active !== false) : [],
      }));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load caregivers.", life: 3000 });
    } finally {
      setLoadingCaregivers(false);
    }
  };

  const refreshResidentsForHome = async (homeId?: string) => {
    const normalizedHomeId = String(homeId || "").trim();
    if (!normalizedHomeId) {
      return;
    }

    setLoadingResidents(true);
    try {
      await backendAPIStore.init();
      const residents = await backendAPIStore.getResidentsForHome(normalizedHomeId);
      setResidentsByHome((prev) => ({
        ...prev,
        [normalizedHomeId]: Array.isArray(residents) ? residents : [],
      }));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load residents.", life: 3000 });
    } finally {
      setLoadingResidents(false);
    }
  };

  const refreshResidentsForHomes = async (homeIds: string[]) => {
    const normalizedHomeIds = Array.from(new Set(
      (Array.isArray(homeIds) ? homeIds : [])
        .map((homeId) => String(homeId || "").trim())
        .filter(Boolean),
    ));

    if (!normalizedHomeIds.length) {
      return;
    }

    setLoadingResidents(true);
    try {
      await backendAPIStore.init();
      const residentResults = await Promise.all(
        normalizedHomeIds.map(async (homeId) => ({
          homeId,
          residents: await backendAPIStore.getResidentsForHome(homeId),
        })),
      );

      setResidentsByHome((prev) => {
        const next = { ...prev };
        residentResults.forEach(({ homeId, residents }) => {
          next[homeId] = Array.isArray(residents) ? residents : [];
        });
        return next;
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load residents.", life: 3000 });
    } finally {
      setLoadingResidents(false);
    }
  };

  useEffect(() => {
    if (loading || !businessHomes?.length || hasLoadedResidentsForAllHomes) {
      return;
    }

    const missingHomeIds = (businessHomes as BusinessHomeProfile[])
      .map((home) => String(home?.homeId || "").trim())
      .filter((homeId) => homeId && !(homeId in residentsByHome));

    if (!missingHomeIds.length) {
      return;
    }

    void refreshResidentsForHomes(missingHomeIds);
  }, [loading, businessHomes, hasLoadedResidentsForAllHomes, residentsByHome]);

  const selectedHomeResidents = selectedHome?.homeId ? (residentsByHome[selectedHome.homeId] || []) : [];
  const activeSelectedHomeResidents = selectedHomeResidents.filter((resident) => isActiveResident(resident));
  const archivedSelectedHomeResidents = selectedHomeResidents.filter((resident) => !isActiveResident(resident));
  const displayedSelectedHomeResidents = showArchivedResidents ? archivedSelectedHomeResidents : activeSelectedHomeResidents;
  const selectedHomeActiveResidentLimit = Number(FACILITY_ACTIVE_RESIDENT_LIMITS[selectedHomeFacilityType || "afh"] || 0);
  const selectedHomeLimitLabel = selectedHomeFacilityType === "esf" ? "Enhanced Services Facilities" : "Adult Family Homes";
  const formatFacilityTypeLabel = (facilityType: any) => {
    const normalizedValue = String(facilityType || "").trim().toLowerCase();
    if (!normalizedValue) {
      return authExperience.facilityTypeOptions.find((option) => option.value === "afh")?.label || "Adult Family Home (AFH)";
    }
    return authExperience.facilityTypeOptions.find((option) => option.value === normalizedValue)?.label
      || normalizedValue.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const activeSelectedHomeResidentIds = activeSelectedHomeResidents
    .map((resident) => String(resident?.residentId || "").trim())
    .filter(Boolean);
  const selectedHomeCaregivers = selectedHome?.homeId
    ? (linkedCaregiversByHome[selectedHome.homeId] || []).filter((caregiver: any) => caregiver?.active !== false)
    : [];
  const selectedHomeCaregiverShares = selectedHome?.homeId ? (caregiverSharesByHome[selectedHome.homeId] || []) : [];
  const getAccessLabelList = (shareItem: any) => {
    return shareItem?.residentId ? ["Operational resident access"] : [];
  };

  useEffect(() => {
    const loadEmarBoard = async () => {
      if (!selectedHome?.homeId || !activeSelectedHomeResidentIds.length) {
        setEmarBoardByResidentId({});
        return;
      }

      setEmarBoardLoading(true);
      try {
        await backendAPIStore.init();
        const todayDate = getLocalIsoDateOnly(new Date());
        const nextBoardState: Record<string, any> = {};
        const residentBoardResults = await Promise.allSettled(
          activeSelectedHomeResidentIds.map(async (residentId) => {
            const [medicationResponse, entryResponse, dailyTrackingItemResponse] = await Promise.all([
              backendAPIStore.getResidentEmarMedications(residentId),
              backendAPIStore.getResidentEmarEntries(residentId, { entryDate: todayDate, limit: 300 }),
              backendAPIStore.getResidentEmarDailyTrackingItems(residentId).catch(() => ({ items: [] })),
            ]);
            return buildEmarBoardResidentStatus(
              residentId,
              medicationResponse?.items,
              filterEmarBoardEntriesForDate(entryResponse?.items, todayDate),
              Array.isArray(dailyTrackingItemResponse?.items) ? dailyTrackingItemResponse.items : [],
            );
          }),
        );
        residentBoardResults.forEach((result, index) => {
          const residentId = activeSelectedHomeResidentIds[index];
          if (!residentId) {
            return;
          }
          if (result.status === "fulfilled") {
            nextBoardState[residentId] = result.value;
            return;
          }
          console.error("Unable to load provider eMAR board resident status");
          nextBoardState[residentId] = buildEmarBoardResidentStatus(residentId, [], [], []);
        });
        setEmarBoardByResidentId(nextBoardState);
      } catch (error) {
        console.error("Unable to load provider eMAR board", error);
      } finally {
        setEmarBoardLoading(false);
      }
    };

    void loadEmarBoard();
  }, [backendAPIStore, selectedHome?.homeId, activeSelectedHomeResidentIds.join("|")]);
  const emarBoardRows = useMemo(
    () => activeSelectedHomeResidents.map((resident) => {
      const residentId = String(resident?.residentId || "").trim();
      return { resident, residentId, emarStatus: emarBoardByResidentId[residentId] || null };
    }).filter((row) => row.emarStatus && String(row.emarStatus.statusLabel || "").trim() !== "Setup Needed"),
    [activeSelectedHomeResidents, emarBoardByResidentId],
  );
  const residentNeedsEmarFollowUp = (status: any) => {
    if (!status?.activeMedicationCount) {
      return false;
    }
    return ((status?.exceptionCount || 0) > 0)
      || ((status?.overdueCount || 0) > 0)
      || ((status?.unreviewedExceptionCount || 0) > 0)
      || ((status?.prnReviewCount || 0) > 0)
      || ((status?.correctedEntryCount || 0) > 0);
  };
  const emarBoardSummary = useMemo(() => {
    return emarBoardRows.reduce((summary, row) => {
      const status = row.emarStatus;
      if (!status?.activeMedicationCount) {
        summary.setup += 1;
      } else if (residentNeedsEmarFollowUp(status)) {
        summary.attention += 1;
      } else if ((status?.dueNowCount || 0) > 0) {
        summary.dueNow += 1;
      } else if ((status?.pendingCount || 0) === 0) {
        summary.complete += 1;
      } else {
        summary.inProgress += 1;
      }
      summary.overdueMeds += status?.overdueCount || 0;
      summary.unreviewedExceptions += status?.unreviewedExceptionCount || 0;
      summary.prnReviews += status?.prnReviewCount || 0;
      return summary;
    }, {
      total: emarBoardRows.length,
      attention: 0,
      dueNow: 0,
      complete: 0,
      setup: 0,
      inProgress: 0,
      overdueMeds: 0,
      unreviewedExceptions: 0,
      prnReviews: 0,
    });
  }, [emarBoardRows]);
  const filteredEmarBoardRows = useMemo(() => {
    const rows = emarBoardRows.filter((row) => {
      const status = row.emarStatus;
      if (emarBoardFilter === "attention") {
        return residentNeedsEmarFollowUp(status);
      }
      if (emarBoardFilter === "due_now") {
        return !!status?.activeMedicationCount && (status?.dueNowCount || 0) > 0 && !residentNeedsEmarFollowUp(status);
      }
      if (emarBoardFilter === "complete") {
        return !!status?.activeMedicationCount && (status?.pendingCount || 0) === 0 && !residentNeedsEmarFollowUp(status);
      }
      return true;
    });

    return rows.sort((first, second) => {
      const firstStatus = first.emarStatus || {};
      const secondStatus = second.emarStatus || {};
      const firstWeight = !firstStatus.activeMedicationCount
        ? 3
        : (firstStatus.exceptionCount || 0) > 0 || (firstStatus.overdueCount || 0) > 0
          ? 0
          : (firstStatus.dueNowCount || 0) > 0
            ? 1
            : (firstStatus.pendingCount || 0) === 0
              ? 4
              : 2;
      const secondWeight = !secondStatus.activeMedicationCount
        ? 3
        : (secondStatus.exceptionCount || 0) > 0 || (secondStatus.overdueCount || 0) > 0
          ? 0
          : (secondStatus.dueNowCount || 0) > 0
            ? 1
            : (secondStatus.pendingCount || 0) === 0
              ? 4
              : 2;
      if (firstWeight !== secondWeight) {
        return firstWeight - secondWeight;
      }
      return `${first.resident?.firstName || ""} ${first.resident?.lastName || ""}`.localeCompare(`${second.resident?.firstName || ""} ${second.resident?.lastName || ""}`);
    });
  }, [emarBoardRows, emarBoardFilter]);
  const adminEmarBoardTableRows = useMemo(() => {
    const query = String(adminEmarBoardSearch || "").trim().toLowerCase();
    return filteredEmarBoardRows
      .map(({ resident, residentId, emarStatus }) => {
        const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident";
        const statusLabel = emarStatus?.statusLabel || "Pending";
        return {
          resident,
          residentId,
          residentName,
          statusLabel,
          severity: emarStatus?.severity || "warning",
          summaryDesktop: emarStatus?.summaryDesktop || "No active meds",
          activeMedicationCount: Number(emarStatus?.activeMedicationCount || 0),
          pendingCount: Number(emarStatus?.pendingCount || 0),
          dueNowCount: Number(emarStatus?.dueNowCount || 0),
          attentionCount: Number(emarStatus?.exceptionCount || 0) + Number(emarStatus?.overdueCount || 0) + Number(emarStatus?.unreviewedExceptionCount || 0) + Number(emarStatus?.prnReviewCount || 0),
          searchIndex: [
            residentName,
            statusLabel,
            emarStatus?.summaryDesktop || "",
            emarStatus?.summaryMobile || "",
            residentId,
          ].join(" ").toLowerCase(),
        };
      })
      .filter((row) => !query || row.searchIndex.includes(query));
  }, [adminEmarBoardSearch, filteredEmarBoardRows]);
  const selectedHomeCaregiverRows = useMemo(
    () => selectedHomeCaregivers.map((caregiver) => {
      const caregiverId = String(caregiver?.caregiverId || caregiver?.userId || "").trim();
      const caregiverShares = selectedHomeCaregiverShares.filter((item: any) => String(item?.caregiverId || "").trim() === caregiverId);
      const sharedResidents = caregiverShares
        .map((shareItem: any) => {
          const residentId = String(shareItem?.residentId || "").trim();
          const matchingResident = selectedHomeResidents.find((resident) => String(resident?.residentId || "").trim() === residentId);
          const residentName = matchingResident
            ? `${matchingResident?.firstName || ""} ${matchingResident?.lastName || ""}`.trim() || "Resident"
            : String(shareItem?.residentName || "").trim() || "Resident";
          return {
            residentId,
            residentName,
            active: matchingResident ? isActiveResident(matchingResident) : true,
            dailyNotesAccessEnabled: true,
            incidentReportsAccessEnabled: true,
            behaviorLogAccessEnabled: true,
            carePlanAccessEnabled: true,
            emarAccessEnabled: true,
            adlAccessEnabled: true,
          };
        })
        .filter((item: any) => Boolean(item?.residentId) && item?.active !== false)
        .sort((first: any, second: any) => String(first?.residentName || "").localeCompare(String(second?.residentName || "")));
      const permissionLabels = Array.from(new Set(sharedResidents.flatMap((resident: any) => getAccessLabelList(resident))));
      const roleTypes = Array.from(new Set(
        [
          String(caregiver?.userRoleType || "").trim().toLowerCase(),
          ...(Array.isArray(caregiver?.roleTypes) ? caregiver.roleTypes : []),
          ...(Array.isArray(caregiver?.selectedRoleTypes) ? caregiver.selectedRoleTypes : []),
        ].filter(Boolean),
      ));
      const homeRoleTypes: string[] = Array.from(new Set<string>(
        (Array.isArray(caregiver?.homeRoleTypes) ? caregiver.homeRoleTypes : roleTypes)
          .map((roleType: any) => String(roleType || "").trim().toLowerCase())
          .filter(Boolean),
      ));
      return {
        caregiver,
        caregiverId,
        caregiverName: `${caregiver?.firstName || ""} ${caregiver?.lastName || ""}`.trim() || caregiver?.email || "Caregiver",
        roleTypes,
        homeRoleTypes,
        userRoleType: String(caregiver?.userRoleType || roleTypes[0] || "").trim().toLowerCase(),
        sharedResidents,
        sharedCount: sharedResidents.length,
        permissionSummary: permissionLabels.length ? permissionLabels.join(", ") : "No tools shared",
        availabilitySummary: formatShiftSummary(caregiver),
        shiftEnabled: caregiver?.shiftEnabled === true,
        mobileAccessEnabled: caregiver?.mobileAccessEnabled !== false,
      };
    }),
    [formatShiftSummary, selectedHomeCaregivers, selectedHomeCaregiverShares, selectedHomeResidents],
  );
  const residentAssignedCaregiverCounts = useMemo(
    () => selectedHomeCaregiverRows.reduce((acc, caregiverRow) => {
      caregiverRow.sharedResidents.forEach((resident: any) => {
        const residentId = String(resident?.residentId || "").trim();
        if (!residentId) {
          return;
        }
        acc[residentId] = (acc[residentId] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>),
    [selectedHomeCaregiverRows],
  );
  const selectedPermissionsCaregiverRow = useMemo(
    () => selectedHomeCaregiverRows.find((caregiverRow) => caregiverRow.caregiverId === selectedCaregiverForPermissions) || selectedHomeCaregiverRows[0] || null,
    [selectedCaregiverForPermissions, selectedHomeCaregiverRows],
  );
  const adminResidentTableRows = useMemo(() => {
    const homeId = String(selectedHome?.homeId || "").trim();
    const query = String(adminResidentSearch || "").trim().toLowerCase();
    return displayedSelectedHomeResidents
      .map((resident, index) => {
        const residentId = String(resident?.residentId || "").trim();
        const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident";
        const assignedCaregiverCount = residentAssignedCaregiverCounts[residentId] || 0;
        const searchIndex = [
          residentName,
          String((resident as any)?.preferredName || "").trim(),
          String((resident as any)?.roomNumber || "").trim(),
          String((resident as any)?.apartmentNumber || "").trim(),
          String((resident as any)?.bedNumber || "").trim(),
          residentId,
        ]
          .join(" ")
          .toLowerCase();
        return {
          resident,
          residentId,
          residentName,
          residentStatusLabel: resident?.active ? "Active" : "Archived",
          residentStatusRank: resident?.active ? 0 : 1,
          assignedCaregiverCount,
          homeId,
          index,
          searchIndex,
        };
      })
      .filter((row) => !query || row.searchIndex.includes(query));
  }, [adminResidentSearch, displayedSelectedHomeResidents, residentAssignedCaregiverCounts, selectedHome?.homeId]);
  const residentTableResidents = useMemo(
    () => isOneHubForCareBrand ? adminResidentTableRows.map((row) => row.resident) : displayedSelectedHomeResidents,
    [adminResidentTableRows, displayedSelectedHomeResidents, isOneHubForCareBrand],
  );
  const residentTablePageResidents = useMemo(
    () => residentTableResidents.slice(residentTableFirst, residentTableFirst + RESIDENT_TABLE_PAGE_SIZE),
    [residentTableFirst, residentTableResidents],
  );

  useEffect(() => {
    setResidentTableFirst(0);
  }, [adminResidentSearch, selectedHome?.homeId, showArchivedResidents]);

  useEffect(() => {
    if (residentTableFirst >= residentTableResidents.length && residentTableFirst > 0) {
      setResidentTableFirst(Math.max(0, Math.floor((residentTableResidents.length - 1) / RESIDENT_TABLE_PAGE_SIZE) * RESIDENT_TABLE_PAGE_SIZE));
    }
  }, [residentTableFirst, residentTableResidents.length]);

  const allShareOptionHints = Object.values(shareOptionHintConfig);

  useEffect(() => {
    if (!selectedHome?.homeId) {
      return;
    }
    if (!isProviderSubscribed) {
      return;
    }
    void refreshCaregiverAccess(selectedHome.homeId);
  }, [selectedHome?.homeId, isProviderSubscribed]);

  useEffect(() => {
    if (!selectedHomeCaregiverRows.length) {
      setSelectedCaregiverForPermissions("");
      return;
    }
    const hasSelectedCaregiver = selectedHomeCaregiverRows.some((caregiverRow) => caregiverRow.caregiverId === selectedCaregiverForPermissions);
    if (!hasSelectedCaregiver) {
      setSelectedCaregiverForPermissions(selectedHomeCaregiverRows[0].caregiverId);
    }
  }, [selectedCaregiverForPermissions, selectedHomeCaregiverRows]);

  const openAddResidentForHome = (homeId?: string) => {
    if (!homeId) return;
    const targetHome = (businessHomes as BusinessHomeProfile[]).find((home) => String(home?.homeId || "").trim() === String(homeId || "").trim()) || null;
    const targetHomeFacilityType = String(targetHome?.facilityType || "").trim().toLowerCase();
    const normalizedTargetHomeFacilityType = targetHomeFacilityType || "afh";
    const targetHomeActiveResidentLimit = Number(FACILITY_ACTIVE_RESIDENT_LIMITS[normalizedTargetHomeFacilityType] || 0);
    const targetHomeResidents = homeId ? (residentsByHome[homeId] || []) : [];
    const activeTargetHomeResidentCount = targetHomeResidents.filter((resident) => isActiveResident(resident)).length;

    if (targetHomeActiveResidentLimit > 0 && activeTargetHomeResidentCount >= targetHomeActiveResidentLimit) {
      const facilityLabel = normalizedTargetHomeFacilityType === "esf" ? "Enhanced Services Facilities" : "Adult Family Homes";
      toast.current?.show({
        severity: "warn",
        summary: "Resident Limit Reached",
        detail: `${facilityLabel} can only have up to ${targetHomeActiveResidentLimit} active residents at a time.`,
        life: 3500,
      });
      return;
    }

    if (homeId === firstHomeId && isStepActive("add-resident-cta")) {
      completeStep("add-resident-cta");
    }
    setSelectedHomeId(homeId);
    setSelectedHomeIdForResident(homeId);
    setOpenResidentModal(true);
  };

  const openEditHomeDialog = async (homeId?: string) => {
    if (!homeId) {
      return;
    }

    try {
      setEditHomeLoading(true);
      await backendAPIStore.init();
      const homeResponse = await backendAPIStore.getBusinessHomeDetails(homeId);
      setEditHomeData(homeResponse || null);
      setOpenEditHomeModal(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || `Unable to load ${facilitySingularLabelLower} details.`,
        life: 3000,
      });
    } finally {
      setEditHomeLoading(false);
    }
  };

  const deleteSelectedHome = (homeId?: string) => {
    if (!homeId) {
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        `Delete this ${facilitySingularLabelLower} permanently?`,
        `This cannot be undone. The ${facilitySingularLabelLower} will be marked inactive, the website will go offline, residents will be marked inactive, and linked staff will be removed.`,
      ),
      header: "Delete Confirmation",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.deleteHome(homeId);
          toast.current?.show({ severity: "success", summary: "Success", detail: `${facilitySingularLabel} deleted`, life: 3000 });
          setAddFlag((prev) => !prev);
          setSelectedHomeId("");
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || `Unable to delete ${facilitySingularLabelLower}.`, life: 3000 });
        }
      },
      reject: () => { },
    });
  };

  const onRemoveCaregiver = (caregiver: any) => {
    if (!selectedHome?.homeId) {
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        `Remove staff member from this ${facilitySingularLabelLower}?`,
        `${caregiver?.firstName || ""} ${caregiver?.lastName || ""}`.trim() || `This staff member will lose access to shared residents for this ${facilitySingularLabelLower}.`,
      ),
      header: "Remove Staff",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.deleteHomeCaregiverLink(String(caregiver?.linkId || ""), selectedHome.homeId || "");
          await refreshCaregiverAccess(selectedHome.homeId);
          toast.current?.show({ severity: "success", summary: "Staff Removed", detail: `Access for this ${facilitySingularLabelLower} has been removed.`, life: 2500 });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to remove staff member.", life: 3000 });
        }
      },
      reject: () => { },
    });
  };

  const confirmActivateResident = (resident: ResidentProfile, homeId: string) => {
    const residentId = String(resident?.residentId || "").trim();
    const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "This resident";
    const canRestoreResident = isAdminUser || Number(providerAllowedResidents || 0) <= 0 || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);
    const targetHome = (businessHomes as BusinessHomeProfile[]).find((home) => String(home?.homeId || "").trim() === String(homeId || "").trim()) || null;
    const targetHomeFacilityType = String(targetHome?.facilityType || "").trim().toLowerCase();
    const normalizedTargetHomeFacilityType = targetHomeFacilityType || "afh";
    const targetHomeActiveResidentLimit = Number(FACILITY_ACTIVE_RESIDENT_LIMITS[normalizedTargetHomeFacilityType] || 0);
    const targetHomeResidents = homeId ? (residentsByHome[homeId] || []) : [];
    const activeTargetHomeResidentCount = targetHomeResidents.filter((item) => isActiveResident(item)).length;

    if (!residentId) {
      return;
    }

    if (targetHomeActiveResidentLimit > 0 && activeTargetHomeResidentCount >= targetHomeActiveResidentLimit) {
      const facilityLabel = normalizedTargetHomeFacilityType === "esf" ? "Enhanced Services Facilities" : "Adult Family Homes";
      toast.current?.show({
        severity: "warn",
        summary: "Resident Limit Reached",
        detail: `${facilityLabel} can only have up to ${targetHomeActiveResidentLimit} active residents at a time.`,
        life: 3500,
      });
      return;
    }

    if (!canRestoreResident) {
      setShowResidentSlotLimitDialog(true);
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        "Restore this resident to the active list?",
        `${residentName} will be active again and available in resident workflows for this ${facilitySingularLabelLower}.`,
      ),
      header: "Activate Resident",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.updateResident({
            ...resident,
            active: true,
          });
          await refreshResidentsForHome(homeId);
          toast.current?.show({
            severity: "success",
            summary: "Resident Activated",
            detail: `${residentName} is back in the active list.`,
            life: 2500,
          });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to activate resident.", life: 3000 });
        }
      },
      reject: () => { },
    });
  };

  const confirmArchiveResident = (resident: ResidentProfile, homeId: string) => {
    const residentId = String(resident?.residentId || "").trim();
    const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "This resident";

    if (!residentId) {
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        "Archive this resident from the active list?",
        `${residentName} will move to archived residents and stay available in your records.`,
      ),
      header: "Archive Resident",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.updateResident({
            ...resident,
            active: false,
          });
          await refreshResidentsForHome(homeId);
          toast.current?.show({
            severity: "success",
            summary: "Resident Archived",
            detail: `${residentName} moved to archived residents.`,
            life: 2500,
          });
        } catch (error: any) {
          if (String(error?.code || "").toUpperCase() === "RESIDENT_ARCHIVE_LIMIT_REACHED") {
            const details = error?.details?.details || error?.details || {};
            setResidentArchiveLimitDetails({
              billingPeriodStart: details?.billingPeriodStart,
              billingPeriodEnd: details?.billingPeriodEnd,
            });
            setShowResidentArchiveLimitDialog(true);
          } else {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to archive resident.", life: 3000 });
          }
        }
      },
      reject: () => { },
    });
  };

  const openShareResidentsDialog = (caregiver: any) => {
    const caregiverId = String(caregiver?.caregiverId || caregiver?.userId || "").trim();
    if (!caregiverId) {
      return;
    }

    const currentShares = selectedHomeCaregiverShares
      .filter((item: any) => String(item?.caregiverId || "").trim() === caregiverId);
    const currentlySharedResidentIds = currentShares
      .map((item: any) => String(item?.residentId || "").trim())
      .filter(Boolean);
    setSelectedCaregiverForSharing(caregiver);
    setSelectedSharedResidentIds(currentlySharedResidentIds);
    setShareDialogVisible(true);
  };

  const openShiftDialog = (caregiver: any) => {
    const normalizedDays = Array.isArray(caregiver?.shiftDays) ? caregiver.shiftDays.map((day: any) => String(day || "").trim().toLowerCase()) : [];
    setSelectedCaregiverForShift(caregiver);
    setShiftEnabled(Boolean(caregiver?.shiftEnabled));
    setShiftDays(normalizedDays);
    setShiftStartTime(String(caregiver?.shiftStartTime || "08:00").trim() || "08:00");
    setShiftEndTime(String(caregiver?.shiftEndTime || "16:00").trim() || "16:00");
    setMobileAccessEnabled(caregiver?.mobileAccessEnabled !== false);
    setAllDayShiftAccess(String(caregiver?.shiftStartTime || "").trim() === "00:00" && String(caregiver?.shiftEndTime || "").trim() === "23:59");
    setShiftDialogVisible(true);
  };

  const onSelectAllDayShiftAccess = (checked: boolean) => {
    setAllDayShiftAccess(checked);
    if (checked) {
      setShiftEnabled(true);
      setShiftStartTime("00:00");
      setShiftEndTime("23:59");
    } else {
      setShiftStartTime("08:00");
      setShiftEndTime("16:00");
    }
  };

  const onSaveCaregiverShift = async () => {
    if (!isProviderSubscribed) {
      openSubscriptionDialog("Add Caregivers");
      return;
    }
    const homeId = String(selectedHome?.homeId || "").trim();
    const caregiverId = String(selectedCaregiverForShift?.caregiverId || selectedCaregiverForShift?.userId || "").trim();
    const linkId = String(selectedCaregiverForShift?.linkId || "").trim();
    if (!homeId || !caregiverId || !linkId) {
      return;
    }
    if (shiftEnabled) {
      if (!shiftDays.length) {
        toast.current?.show({
          severity: "warn",
          summary: "Shift Days Required",
          detail: "Select at least one day when scheduled access is enabled.",
          life: 3000,
        });
        return;
      }
      if (!allDayShiftAccess && (!String(shiftStartTime || "").trim() || !String(shiftEndTime || "").trim())) {
        toast.current?.show({
          severity: "warn",
          summary: "Shift Time Required",
          detail: "Select start and end times, or use All day access.",
          life: 3000,
        });
        return;
      }
    }

    setSavingCaregiverShift(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateHomeCaregiverLinkShift({
        homeId,
        caregiverId,
        linkId,
        shiftEnabled,
        shiftDays,
        shiftStartTime,
        shiftEndTime,
        shiftTimezone: "America/Los_Angeles",
        mobileAccessEnabled,
      });
      await refreshCaregiverAccess(homeId);
      setShiftDialogVisible(false);
      setSelectedCaregiverForShift(null);
      toast.current?.show({
        severity: "success",
        summary: "Shift Updated",
        detail: shiftEnabled ? "Caregiver working hours have been saved." : "Caregiver shift has been cleared.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update caregiver shift.", life: 3000 });
    } finally {
      setSavingCaregiverShift(false);
    }
  };

  const onSaveCaregiverShares = async () => {
    if (!isProviderSubscribed) {
      openSubscriptionDialog("Add Caregivers");
      return;
    }
    const homeId = String(selectedHome?.homeId || "").trim();
    const caregiverId = String(selectedCaregiverForSharing?.caregiverId || selectedCaregiverForSharing?.userId || "").trim();
    if (!homeId || !caregiverId) {
      return;
    }

    const currentShares = selectedHomeCaregiverShares
      .filter((item: any) => String(item?.caregiverId || "").trim() === caregiverId);
    const currentShareIds = currentShares
      .map((item: any) => String(item?.residentId || "").trim())
      .filter(Boolean);

    const shareIdsToAdd = selectedSharedResidentIds.filter((residentId) => !currentShareIds.includes(residentId));
    const shareIdsToRemove = currentShareIds.filter((residentId) => !selectedSharedResidentIds.includes(residentId));

    setSavingCaregiverShares(true);
    try {
      await backendAPIStore.init();
      await Promise.all([
        ...shareIdsToAdd.map((residentId) =>
          backendAPIStore.addCaregiverResidentShare({
            homeId,
            caregiverId,
            residentId,
          }),
        ),
        ...shareIdsToRemove.map((residentId) =>
          backendAPIStore.deleteCaregiverResidentShare(homeId, caregiverId, residentId),
        ),
      ]);
      await refreshCaregiverAccess(homeId);
      setShareDialogVisible(false);
      setSelectedCaregiverForSharing(null);
      toast.current?.show({ severity: "success", summary: "Residents Shared", detail: "Caregiver resident access has been updated.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update resident sharing.", life: 3000 });
    } finally {
      setSavingCaregiverShares(false);
    }
  };

  const renderResidentActions = (resident: ResidentProfile, homeId: string, index: number) => {
    const residentId = resident.residentId;
    const shortcutButtonStyle = (color: string, background: string, border: string) => ({
      borderRadius: "999px",
      padding: "0.28rem 0.72rem",
      background,
      color,
      border: `1px solid ${border}`,
      boxShadow: "none",
      fontSize: "0.8rem",
      fontWeight: 700,
    });
    return (
      <div className="flex flex-wrap gap-2">
        {isActiveResident(resident) ? (
          <>
            <Button
              type="button"
              label="Search Care Plan"
              text
              icon="pi pi-search"
              iconPos="left"
              disabled={!residentId}
              data-tour={homeId === firstHomeId && index === 0 ? "provider-generate-ncp" : undefined}
              style={shortcutButtonStyle("#1d4ed8", "#eff6ff", "#bfdbfe")}
              onClick={() => {
                if (residentId) {
                  runSubscribedProviderAction("Search Care Plan", () => {
                    if (homeId === firstHomeId && index === 0 && isStepActive("generate-ncp")) {
                      completeStep("generate-ncp");
                    }
                    navigate(RouterAddress.getProviderResidentCarePlanPagePath(residentId, String(homeId || "")));
                  });
                }
              }}
            />
            <Button
              type="button"
              label="eMAR"
              text
              icon="pi pi-heart-fill"
              iconPos="left"
              disabled={!residentId}
              style={shortcutButtonStyle("#0369a1", "#f0f9ff", "#bae6fd")}
              onClick={() => {
                if (residentId) {
                  runSubscribedProviderAction("eMAR", () => {
                    navigate(RouterAddress.getProviderResidentEmarPagePath(residentId, String(homeId || "")));
                  });
                }
              }}
            />
            <Button
              type="button"
              label="Behavior Tracker"
              text
              icon="pi pi-clock"
              iconPos="left"
              disabled={!residentId}
              style={shortcutButtonStyle("#9a3412", "#fff7ed", "#fdba74")}
              onClick={() => {
                if (residentId) {
                  runSubscribedProviderAction("Behavior Tracker", () => {
                    navigate(RouterAddress.getProviderResidentDailyBehaviorLogPagePath(residentId, undefined, String(homeId || "")));
                  });
                }
              }}
            />
            <Button
              type="button"
              label="Incident Reports"
              text
              icon="pi pi-exclamation-circle"
              iconPos="left"
              disabled={!residentId}
              style={shortcutButtonStyle("#b91c1c", "#fef2f2", "#fecaca")}
              onClick={() => {
                if (residentId) {
                  runSubscribedProviderAction("Incident Reports", () => {
                    navigate(RouterAddress.getProviderResidentIncidentReportPagePath(residentId, undefined, String(homeId || "")));
                  });
                }
              }}
            />
            <Button
              type="button"
              label="Progress Notes"
              text
              icon="pi pi-pencil"
              iconPos="left"
              disabled={!residentId}
              style={shortcutButtonStyle("#0f766e", "#ecfeff", "#99f6e4")}
              onClick={() => {
                if (residentId) {
                  runSubscribedProviderAction("Daily Notes", () => {
                    navigate(RouterAddress.getProviderResidentDailyNotesPagePath(residentId, undefined, String(homeId || "")));
                  });
                }
              }}
            />
            <Button
              type="button"
              label="Use AI"
              text
              icon="pi pi-sparkles"
              iconPos="left"
              disabled={!residentId}
              style={shortcutButtonStyle("#7c3aed", "#f5f3ff", "#ddd6fe")}
              onClick={() => openResidentAiDialog(resident, homeId)}
            />
            {SHOW_ADL_ENTRY_POINTS ? (
              <Button
                type="button"
                label="ADL Tracking"
                text
                icon="pi pi-check-square"
                iconPos="left"
                disabled={!residentId}
                style={shortcutButtonStyle("#7c2d12", "#fff7ed", "#fdba74")}
                onClick={() => {
                  if (residentId) {
                    runSubscribedProviderAction("ADL Tracking", () => {
                      navigate(RouterAddress.getProviderResidentAdlPagePath(residentId, String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  const getResidentToolItems = (resident: ResidentProfile, homeId: string, index: number) => {
    const residentId = String(resident?.residentId || "").trim();
    if (!residentId) {
      return [];
    }

    const archived = !isActiveResident(resident);
    const withArchivedReadOnly = (path: string) => archived
      ? `${path}${path.includes("?") ? "&" : "?"}archived=true`
      : path;

    return [
      ...(SHOW_RESIDENT_SCHEDULES ? [{
        label: "Schedules",
        icon: "pi pi-calendar",
        command: () => runSubscribedProviderAction("Schedules", () => {
          navigate(RouterAddress.getResidentSchedulesPagePath(String(homeId || ""), residentId));
        }),
      }] : []),
      {
        label: "Behavior Tracker",
        icon: "pi pi-clock",
        command: () => runSubscribedProviderAction("Behavior Tracker", () => {
          navigate(withArchivedReadOnly(RouterAddress.getProviderResidentDailyBehaviorLogPagePath(residentId, undefined, String(homeId || ""))));
        }),
      },
      {
        label: "Incident Reports",
        icon: "pi pi-exclamation-circle",
        command: () => runSubscribedProviderAction("Incident Reports", () => {
          navigate(withArchivedReadOnly(RouterAddress.getProviderResidentIncidentReportPagePath(residentId, undefined, String(homeId || ""))));
        }),
      },
      {
        label: "Progress Notes",
        icon: "pi pi-pencil",
        command: () => runSubscribedProviderAction("Daily Notes", () => {
          navigate(withArchivedReadOnly(RouterAddress.getProviderResidentDailyNotesPagePath(residentId, undefined, String(homeId || ""))));
        }),
      },
      ...(archived ? [{
        label: "ADL",
        icon: "pi pi-check-square",
        command: () => runSubscribedProviderAction("ADL", () => {
          navigate(withArchivedReadOnly(RouterAddress.getProviderResidentAdlPagePath(residentId, String(homeId || ""))));
        }),
      }] : []),
      {
        label: "Search Care Plan",
        icon: "pi pi-search",
        command: () => runSubscribedProviderAction("Search Care Plan", () => {
          if (homeId === firstHomeId && index === 0 && isStepActive("generate-ncp")) {
            completeStep("generate-ncp");
          }
          navigate(RouterAddress.getProviderResidentCarePlanPagePath(residentId, String(homeId || "")));
        }),
      },
      ...(!archived && isAdminUser ? [{
        label: "Archive",
        icon: "pi pi-archive",
        command: () => confirmArchiveResident(resident, homeId),
      }] : []),
    ];
  };

  const renderResidentRow = (resident: ResidentProfile, homeId: string, index: number) => {
    const residentName = `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Resident";
    const residentId = String(resident?.residentId || "").trim();
    const assignedCaregiverCount = residentAssignedCaregiverCounts[residentId] || 0;
    const residentToolItems = getResidentToolItems(resident, homeId, index);
    const canRestoreResident = isAdminUser || Number(providerAllowedResidents || 0) <= 0 || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);

    return (
      <React.Fragment key={resident.residentId || `${homeId}-${index}`}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 1fr) minmax(560px, 2.35fr) minmax(220px, 1.1fr)",
            gap: "0.75rem",
            padding: "0.9rem 1rem",
            borderBottom: "1px solid #eef2f7",
            alignItems: "start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
              {residentName}
            </div>
            {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
              <Tag value={`Schedules ${Number((resident as any)?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
              {Number((resident as any)?.scheduleSummary?.missedCount || 0) > 0 ? (
                <Tag value={`Missed ${Number((resident as any)?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded />
              ) : null}
            </div> : null}
            {!isActiveResident(resident) ? (
              <div style={{ marginTop: "0.28rem" }}>
                <Tag value="Archived" severity="secondary" rounded />
              </div>
            ) : null}
            {assignedCaregiverCount > 0 ? (
              <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
                {assignedCaregiverCount} staff member{assignedCaregiverCount === 1 ? "" : "s"} assigned
              </div>
            ) : null}
          </div>
          {!mobile ? (
            <div style={{ color: "#0f172a", fontWeight: 700 }}>
              {assignedCaregiverCount > 0 ? assignedCaregiverCount : "—"}
            </div>
          ) : (
            <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5 }}>
              {assignedCaregiverCount > 0 ? `${assignedCaregiverCount} staff member${assignedCaregiverCount === 1 ? "" : "s"}` : "No staff assigned"}
            </div>
          )}
          <div
            className="flex gap-2"
            style={{
              flexWrap: mobile ? "wrap" : "nowrap",
              overflowX: mobile ? "visible" : "auto",
              minWidth: 0,
            }}
          >
            {hasResidentCarePlan(resident) ? <Button
              type="button"
              label="Dashboard"
              text
              icon="pi pi-user"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#9a3412",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentDetailsPagePath(homeId, resident.residentId));
                }
              }}
            /> : null}
            <Button
              type="button"
              label="Binder"
              text
              icon="pi pi-book"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#0f766e",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
                background: "#f0fdfa",
                border: "1px solid #99f6e4",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentBinderPagePath(homeId, resident.residentId));
                }
              }}
            />
            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="Manage Care Plan"
                text
                icon="pi pi-file-edit"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#6d28d9",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f5f3ff",
                  border: "1px solid #ddd6fe",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    navigate(getResidentCarePlanPath(homeId, resident.residentId));
                  }
                }}
              />
            ) : null}
            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="eMAR"
                text
                icon="pi pi-medical-drip"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#0369a1",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    runSubscribedProviderAction("eMAR", () => {
                      navigate(RouterAddress.getProviderResidentEmarPagePath(String(resident.residentId || ""), String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
          </div>
          <div className="flex justify-content-end" style={{ minWidth: 0 }}>
            {residentToolItems.length ? (
              <>
              <ActionMenuButton
                label="Daily Tracker"
                size="small"
                severity="secondary"
                rounded
                outlined
                model={residentToolItems}
                disabled={!resident.residentId}
                style={residentToolBadgeStyle(mobile ? undefined : "11.5rem")}
              />
              {isActiveResident(resident) && authExperience.featureFlags.providerTools.entryTracker ? <ResidentEntryTrackerButton
                residentId={String(resident.residentId || "")}
                residentName={residentName}
                residentEntryStatus={resident.residentEntryStatus}
                disabled={!resident.residentId}
                style={residentToolBadgeStyle(mobile ? undefined : "11.5rem")}
                createEntry={(targetResidentId, payload) => backendAPIStore.createResidentMovementEntry(targetResidentId, payload)}
                dashboardPath={`${RouterAddress.getProviderResidentEntryTrackerPagePath(String(resident.residentId || ""))}?section=monthly`}
              /> : null}
              </>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderResidentTableRow = (resident: ResidentProfile, homeId: string, index: number) => {
    const residentName = `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Resident";
    const residentId = String(resident?.residentId || "").trim();
    const assignedCaregiverCount = residentAssignedCaregiverCounts[residentId] || 0;
    const residentToolItems = getResidentToolItems(resident, homeId, index);
    const canRestoreResident = isAdminUser || Number(providerAllowedResidents || 0) <= 0 || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);
    return (
      <React.Fragment key={`resident-table-${resident.residentId || `${homeId}-${index}`}`}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.55fr) minmax(320px, 1.9fr) minmax(140px, 0.9fr)",
            gap: "0.75rem",
            padding: "0.9rem 1rem",
            borderBottom: "1px solid #eef2f7",
            alignItems: "start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
              {residentName}
            </div>
            {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
              <Tag value={`Schedules ${Number((resident as any)?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
              {Number((resident as any)?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number((resident as any)?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}
            </div> : null}
            {!isActiveResident(resident) ? (
              <div style={{ marginTop: "0.28rem" }}>
                <Tag value="Archived" severity="secondary" rounded />
              </div>
            ) : null}
            {assignedCaregiverCount > 0 ? (
              <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
                {assignedCaregiverCount} staff member{assignedCaregiverCount === 1 ? "" : "s"} assigned
              </div>
            ) : null}
          </div>
          <div className="flex gap-2 flex-wrap">
            {hasResidentCarePlan(resident) ? <Button
              type="button"
              label="Dashboard"
              text
              icon="pi pi-user"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#9a3412",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentDetailsPagePath(homeId, resident.residentId));
                }
              }}
            /> : null}
            <Button
              type="button"
              label="Binder"
              text
              icon="pi pi-book"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#0f766e",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                background: "#f0fdfa",
                border: "1px solid #99f6e4",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentBinderPagePath(homeId, resident.residentId));
                }
              }}
            />
            {renderSetupRequiredBadge(resident, homeId)}
            {isActiveResident(resident) && hasResidentCarePlan(resident) ? (
              <Button
                type="button"
                label="Manage Care Plan"
                text
                icon="pi pi-file-edit"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#6d28d9",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  background: "#f5f3ff",
                  border: "1px solid #ddd6fe",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    navigate(getResidentCarePlanPath(homeId, resident.residentId));
                  }
                }}
              />
            ) : null}

            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="eMAR"
                text
                icon="pi pi-medkit"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#0369a1",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    runSubscribedProviderAction("eMAR", () => {
                      navigate(RouterAddress.getProviderResidentEmarPagePath(String(resident.residentId || ""), String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
            {SHOW_ADL_ENTRY_POINTS && isActiveResident(resident) ? (
              <Button
                type="button"
                label="ADL"
                text
                icon="pi pi-check-square"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#9a3412",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    runSubscribedProviderAction("ADL Tracking", () => {
                      navigate(RouterAddress.getProviderResidentAdlPagePath(String(resident.residentId || ""), String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
            {isActiveResident(resident) && hasResidentCarePlan(resident) ? (
              <Button
                type="button"
                label="Use AI"
                text
                icon="pi pi-sparkles"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#7c3aed",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f5f3ff",
                  border: "1px solid #ddd6fe",
                  borderRadius: "999px",
                }}
                onClick={() => openResidentAiDialog(resident, homeId)}
              />
            ) : null}
            {!isActiveResident(resident) ? (
              <Button
                type="button"
                label="Activate"
                severity="warning"
                rounded
                outlined
                disabled={!residentId}
                onClick={() => confirmActivateResident(resident, homeId)}
              />
            ) : null}
          </div>
          <div className="flex justify-content-end">
            {residentToolItems.length ? (
              <>
              <ActionMenuButton
                label="Daily Tracker"
                size="small"
                severity="secondary"
                rounded
                outlined
                model={residentToolItems}
                disabled={!resident.residentId}
                style={residentToolBadgeStyle()}
              />
              {isActiveResident(resident) && authExperience.featureFlags.providerTools.entryTracker ? <ResidentEntryTrackerButton
                residentId={residentId}
                residentName={residentName}
                residentEntryStatus={resident.residentEntryStatus}
                disabled={!residentId}
                style={residentToolBadgeStyle()}
                createEntry={(targetResidentId, payload) => backendAPIStore.createResidentMovementEntry(targetResidentId, payload)}
                dashboardPath={`${RouterAddress.getProviderResidentEntryTrackerPagePath(residentId)}?section=monthly`}
              /> : null}
              </>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderResidentDesktopTableRow = (resident: ResidentProfile, homeId: string, index: number) => {
    const residentName = `${resident.firstName || ""} ${resident.lastName || ""}`.trim() || "Resident";
    const residentId = String(resident?.residentId || "").trim();
    const assignedCaregiverCount = residentAssignedCaregiverCounts[residentId] || 0;
    const residentToolItems = getResidentToolItems(resident, homeId, index);
    const canRestoreResident = isAdminUser || Number(providerAllowedResidents || 0) <= 0 || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);

    return (
      <tr key={`resident-table-desktop-${resident.residentId || `${homeId}-${index}`}`} style={{ borderBottom: "1px solid #eef2f7" }}>
        <td style={{ padding: "0.95rem 1rem", verticalAlign: "top", minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
            {residentName}
          </div>
          {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
            <Tag value={`Schedules ${Number((resident as any)?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
            {Number((resident as any)?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number((resident as any)?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}
          </div> : null}
          {!isActiveResident(resident) ? (
            <div style={{ marginTop: "0.28rem" }}>
              <Tag value="Archived" severity="secondary" rounded />
            </div>
          ) : null}
          {assignedCaregiverCount > 0 ? (
            <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
              {assignedCaregiverCount} staff member{assignedCaregiverCount === 1 ? "" : "s"} assigned
            </div>
          ) : null}
        </td>
        <td style={{ padding: "0.95rem 1rem", verticalAlign: "top" }}>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              label="Dashboard"
              text
              icon="pi pi-user"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#9a3412",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentDetailsPagePath(homeId, resident.residentId));
                }
              }}
            />
            <Button
              type="button"
              label="Binder"
              text
              icon="pi pi-book"
              iconPos="left"
              disabled={!resident.residentId}
              style={{
                color: "#0f766e",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
                background: "#f0fdfa",
                border: "1px solid #99f6e4",
                borderRadius: "999px",
              }}
              onClick={() => {
                if (resident.residentId) {
                  navigate(RouterAddress.getResidentBinderPagePath(homeId, resident.residentId));
                }
              }}
            />
            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="Manage Care Plan"
                text
                icon="pi pi-file-edit"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#6d28d9",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f5f3ff",
                  border: "1px solid #ddd6fe",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    navigate(getResidentCarePlanPath(homeId, resident.residentId));
                  }
                }}
              />
            ) : null}
            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="Use AI"
                text
                icon="pi pi-sparkles"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#7c3aed",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f5f3ff",
                  border: "1px solid #ddd6fe",
                  borderRadius: "999px",
                }}
                onClick={() => openResidentAiDialog(resident, homeId)}
              />
            ) : null}
            {isActiveResident(resident) ? (
              <Button
                type="button"
                label="eMAR"
                text
                icon="pi pi-medkit"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#0369a1",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    runSubscribedProviderAction("eMAR", () => {
                      navigate(RouterAddress.getProviderResidentEmarPagePath(String(resident.residentId || ""), String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
            {SHOW_ADL_ENTRY_POINTS && isActiveResident(resident) ? (
              <Button
                type="button"
                label="ADL"
                text
                icon="pi pi-check-square"
                iconPos="left"
                disabled={!resident.residentId}
                style={{
                  color: "#9a3412",
                  fontWeight: 700,
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.74rem",
                  whiteSpace: "nowrap",
                  background: "#fff7ed",
                  border: "1px solid #fdba74",
                  borderRadius: "999px",
                }}
                onClick={() => {
                  if (resident.residentId) {
                    runSubscribedProviderAction("ADL Tracking", () => {
                      navigate(RouterAddress.getProviderResidentAdlPagePath(String(resident.residentId || ""), String(homeId || "")));
                    });
                  }
                }}
              />
            ) : null}
            {!isActiveResident(resident) ? (
              <Button
                type="button"
                label="Activate"
                severity="warning"
                rounded
                outlined
                disabled={!residentId}
                onClick={() => confirmActivateResident(resident, homeId)}
              />
            ) : null}
          </div>
        </td>
        <td style={{ padding: "0.95rem 1rem", verticalAlign: "top", textAlign: "right", whiteSpace: "nowrap" }}>
          {residentToolItems.length ? (
            <>
            <ActionMenuButton
              label="Daily Tracker"
              size="small"
              severity="secondary"
              rounded
              outlined
              model={residentToolItems}
              disabled={!resident.residentId}
              style={residentToolBadgeStyle("11.5rem")}
            />
            {isActiveResident(resident) && authExperience.featureFlags.providerTools.entryTracker ? <ResidentEntryTrackerButton
              residentId={residentId}
              residentName={residentName}
              residentEntryStatus={resident.residentEntryStatus}
              disabled={!residentId}
              style={residentToolBadgeStyle("11.5rem")}
              createEntry={(targetResidentId, payload) => backendAPIStore.createResidentMovementEntry(targetResidentId, payload)}
              dashboardPath={`${RouterAddress.getProviderResidentEntryTrackerPagePath(residentId)}?section=monthly`}
            /> : null}
            </>
          ) : (
            <span style={{ color: "#94a3b8" }}>—</span>
          )}
        </td>
      </tr>
    );
  };

  const renderAdminResidentNameCell = (row: any) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
        {row.residentName}
      </div>
      {SHOW_RESIDENT_SCHEDULES ? <div className="flex align-items-center gap-2 flex-wrap" style={{ marginTop: "0.3rem" }}>
        <Tag value={`Schedules ${Number(row?.resident?.scheduleSummary?.activeCount || 0)}`} severity="info" rounded />
        {Number(row?.resident?.scheduleSummary?.missedCount || 0) > 0 ? <Tag value={`Missed ${Number(row?.resident?.scheduleSummary?.missedCount || 0)}`} severity="danger" rounded /> : null}
      </div> : null}
      {row.assignedCaregiverCount > 0 ? (
        <div style={{ color: "#475569", fontSize: "0.84rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
          {row.assignedCaregiverCount} staff member{row.assignedCaregiverCount === 1 ? "" : "s"} assigned
        </div>
      ) : (
        <div style={{ color: "#94a3b8", fontSize: "0.84rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
          No staff assigned
        </div>
      )}
    </div>
  );

  const renderAdminResidentShortcutsCell = (row: any) => {
    const residentId = String(row.resident?.residentId || "").trim();

    return (
      <div className="flex gap-2 flex-wrap">
        <Button
          type="button"
          label="Resident Details"
          text
          icon="pi pi-user"
          iconPos="left"
          disabled={!residentId}
          style={{
            color: "#9a3412",
            fontWeight: 700,
            padding: "0.2rem 0.6rem",
            fontSize: "0.74rem",
            whiteSpace: "nowrap",
            background: "#fff7ed",
            border: "1px solid #fdba74",
            borderRadius: "999px",
          }}
          onClick={() => {
            if (residentId) {
              navigate(RouterAddress.getResidentDetailsPagePath(row.homeId, residentId));
            }
          }}
        />
        <Button
          type="button"
          label="Binder"
          text
          icon="pi pi-book"
          iconPos="left"
          disabled={!residentId}
          style={{
            color: "#0f766e",
            fontWeight: 700,
            padding: "0.2rem 0.6rem",
            fontSize: "0.74rem",
            whiteSpace: "nowrap",
            background: "#f0fdfa",
            border: "1px solid #99f6e4",
            borderRadius: "999px",
          }}
          onClick={() => {
            if (residentId) {
              navigate(RouterAddress.getResidentBinderPagePath(row.homeId, residentId));
            }
          }}
        />
        {renderSetupRequiredBadge(row.resident, row.homeId)}
        {row.resident?.active && hasResidentCarePlan(row.resident) ? (
          <Button
            type="button"
            label="Manage Care Plan"
            text
            icon="pi pi-file-edit"
            iconPos="left"
            disabled={!residentId}
            style={{
              color: "#6d28d9",
              fontWeight: 700,
              padding: "0.2rem 0.6rem",
              fontSize: "0.74rem",
              whiteSpace: "nowrap",
              background: "#f5f3ff",
              border: "1px solid #ddd6fe",
              borderRadius: "999px",
            }}
            onClick={() => {
              if (residentId) {
                navigate(getResidentCarePlanPath(row.homeId, residentId));
              }
            }}
          />
        ) : null}
        {row.resident?.active && hasResidentCarePlan(row.resident) ? (
          <Button
            type="button"
            label="Use AI"
            text
            icon="pi pi-sparkles"
            iconPos="left"
            disabled={!residentId}
            style={{
              color: "#7c3aed",
              fontWeight: 700,
              padding: "0.2rem 0.6rem",
              fontSize: "0.74rem",
              whiteSpace: "nowrap",
              background: "#f5f3ff",
              border: "1px solid #ddd6fe",
              borderRadius: "999px",
            }}
            onClick={() => openResidentAiDialog(row.resident, row.homeId)}
          />
        ) : null}
        {row.resident?.active ? (
          <Button
            type="button"
            label="eMAR"
            text
            icon="pi pi-medkit"
            iconPos="left"
            disabled={!residentId}
            style={{
              color: "#0369a1",
              fontWeight: 700,
              padding: "0.2rem 0.6rem",
              fontSize: "0.74rem",
              whiteSpace: "nowrap",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: "999px",
            }}
            onClick={() => {
              if (residentId) {
                runSubscribedProviderAction("eMAR", () => {
                  navigate(RouterAddress.getProviderResidentEmarPagePath(String(residentId || ""), String(row.homeId || "")));
                });
              }
            }}
          />
        ) : null}
        {SHOW_ADL_ENTRY_POINTS && row.resident?.active ? (
          <Button
            type="button"
            label="ADL"
            text
            icon="pi pi-check-square"
            iconPos="left"
            disabled={!residentId}
            style={{
              color: "#9a3412",
              fontWeight: 700,
              padding: "0.2rem 0.6rem",
              fontSize: "0.74rem",
              whiteSpace: "nowrap",
              background: "#fff7ed",
              border: "1px solid #fdba74",
              borderRadius: "999px",
            }}
            onClick={() => {
              if (residentId) {
                runSubscribedProviderAction("ADL Tracking", () => {
                  navigate(RouterAddress.getProviderResidentAdlPagePath(String(residentId || ""), String(row.homeId || "")));
                });
              }
            }}
          />
        ) : null}
      </div>
    );
  };

  const renderAdminResidentDailyActionsCell = (row: any) => {
    const residentToolItems = getResidentToolItems(row.resident, row.homeId, row.index);
    const canRestoreResident = isAdminUser || Number(providerAllowedResidents || 0) <= 0 || totalActiveResidentCountAcrossHomes < Number(providerAllowedResidents || 0);

    return (
      <div className="flex gap-2 flex-wrap justify-content-end">
        {row.resident?.active ? (
          <>
            <ActionMenuButton
              label="Daily Tracker"
              size="small"
              severity="secondary"
              rounded
              outlined
              model={residentToolItems}
              disabled={!row.resident?.residentId}
              style={residentToolBadgeStyle()}
            />
            {authExperience.featureFlags.providerTools.entryTracker ? <ResidentEntryTrackerButton
              residentId={String(row.resident?.residentId || "")}
              residentName={row.residentName}
              residentEntryStatus={row.resident?.residentEntryStatus}
              disabled={!row.resident?.residentId}
              style={residentToolBadgeStyle()}
              createEntry={(targetResidentId, payload) => backendAPIStore.createResidentMovementEntry(targetResidentId, payload)}
              dashboardPath={`${RouterAddress.getProviderResidentEntryTrackerPagePath(String(row.resident?.residentId || ""))}?section=monthly`}
            /> : null}
          </>
        ) : (
          <Button
            type="button"
            label="Activate"
            severity="warning"
            rounded
            outlined
            disabled={!row.resident?.residentId}
            onClick={() => confirmActivateResident(row.resident, row.homeId)}
          />
        )}
      </div>
    );
  };

  const renderAdminEmarResidentCell = (row: any) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
        {row.residentName}
      </div>
      <div style={{ color: "#64748b", fontSize: "0.84rem", lineHeight: 1.5, marginTop: "0.22rem" }}>
        {row.activeMedicationCount > 0 ? `${row.activeMedicationCount} active meds` : "No active meds"}
      </div>
    </div>
  );

  const renderAdminEmarStatusCell = (row: any) => (
    <Tag value={row.statusLabel} severity={row.severity} rounded />
  );

  const renderAdminEmarTodayCell = (row: any) => (
    <div style={{ color: "#475569", fontSize: "0.85rem", lineHeight: 1.4, minWidth: 0 }}>
      {row.summaryDesktop}
    </div>
  );

  const renderAdminEmarActionCell = (row: any) => (
    <div className="flex justify-content-end">
      <Button
        type="button"
        label="Open eMAR"
        text
        icon="pi pi-table"
        iconPos="left"
        style={{
          color: "#7c3aed",
          fontWeight: 700,
          padding: "0.15rem 0.5rem",
          fontSize: "0.74rem",
          background: "#f5f3ff",
          border: "1px solid #ddd6fe",
          borderRadius: "999px",
        }}
        onClick={() => runSubscribedProviderAction("eMAR", () => {
          navigate(RouterAddress.getProviderResidentEmarPagePath(row.residentId, String(selectedHome?.homeId || "")));
        })}
      />
    </div>
  );

  if (!userRoleType) {
    return <Unauthorized />;
  }

  if (normalizedPathname === RouterAddress.ADMIN_HOME.toLowerCase()) {
    return <Navigate to={RouterAddress.PROVIDER_HOME} replace />;
  }

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }}>
      <Toast ref={toast} />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName={subscriptionFeatureName}
        details={subscriptionFeatureDetails}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <Dialog
        header="Resident slots are full"
        visible={showResidentSlotLimitDialog}
        modal
        style={mobile ? { width: "92vw", maxWidth: "92vw" } : { width: "34rem", maxWidth: "90vw" }}
        onHide={() => setShowResidentSlotLimitDialog(false)}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={() => setShowResidentSlotLimitDialog(false)}
            />
            <Button
              type="button"
              label="View Subscription"
              icon="pi pi-credit-card"
              severity="warning"
              rounded
              onClick={() => {
                setShowResidentSlotLimitDialog(false);
                navigate(RouterAddress.BILLING);
              }}
            />
          </div>
        )}
      >
        <div style={{ color: "#334155", lineHeight: 1.6 }}>
          You are using all active resident slots included with your subscription. You can add more resident slots to your subscription or archive an active resident before restoring this resident.
        </div>
      </Dialog>
      <Dialog
        header="Archive limit reached"
        visible={showResidentArchiveLimitDialog}
        modal
        style={mobile ? { width: "92vw", maxWidth: "92vw" } : { width: "34rem", maxWidth: "90vw" }}
        onHide={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }} />
            <Button type="button" label="View Subscription" icon="pi pi-credit-card" severity="warning" rounded onClick={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); navigate(RouterAddress.BILLING); }} />
          </div>
        )}
      >
        <div style={{ color: "#334155", lineHeight: 1.6 }}>
          You have reached the maximum number of resident archives allowed in this billing period.
          {residentArchiveLimitDetails.billingPeriodStart && residentArchiveLimitDetails.billingPeriodEnd && (
            <div style={{ marginTop: "0.75rem", fontWeight: 600 }}>
              Billing period: {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodStart))} – {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodEnd))}
            </div>
          )}
          <div style={{ marginTop: "0.75rem" }}>You can archive more residents when the next billing period begins or after updating your subscription.</div>
        </div>
      </Dialog>
      <Dialog
        header="Resident AI Tools"
        visible={residentAiDialogVisible}
        style={mobile ? { width: "95vw", maxWidth: "95vw" } : { width: "60rem", maxWidth: "90vw" }}
        onHide={closeResidentAiDialog}
        modal
      >
        <div className="flex flex-column gap-3">
          <div style={{ color: "#64748b", lineHeight: 1.6 }}>
            Choose a tool to open directly in the resident's AI workspace.
          </div>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              overflow: "hidden",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 190px) minmax(0, 1fr)",
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                fontWeight: 700,
                color: "#0f172a",
                fontSize: "0.85rem",
              }}
            >
              <div style={{ padding: "0.75rem 0.9rem" }}>Tool</div>
              {!mobile ? <div style={{ padding: "0.75rem 0.9rem" }}>What it does</div> : null}
            </div>

            {[
              {
                label: "Summarize Assessment",
                description: "Generates a concise summary of the resident's assessment document.",
                icon: "pi pi-file-edit",
                action: () => openResidentAiTool("summarize-assessment"),
              },
              {
                label: "Compare Assessments",
                description: "Highlights differences between two assessment documents.",
                icon: "pi pi-clone",
                action: () => openResidentAiTool("compare-assessments"),
              },
              {
                label: "Compare Care Plans",
                description: "Highlights differences between two care plan versions.",
                icon: "pi pi-list-check",
                action: () => openResidentAiTool("compare-care-plans"),
              },
            ].map((item, index, array) => (
              <div
                key={item.label}
                style={{
                  borderBottom: index === array.length - 1 ? "none" : "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 190px) minmax(0, 1fr) auto",
                    alignItems: "start",
                  }}
                >
                  <div style={{ padding: "0.9rem" }}>
                    <div className="flex align-items-center gap-2" style={{ color: "#7c3aed", fontWeight: 700 }}>
                      <i className={item.icon} />
                      <span>{item.label}</span>
                    </div>
                    {mobile ? (
                      <div style={{ color: "#64748b", fontSize: "0.84rem", lineHeight: 1.55, marginTop: "0.45rem" }}>
                        {item.description}
                      </div>
                    ) : null}
                  </div>
                  {!mobile ? (
                    <div style={{ padding: "0.9rem", color: "#475569", fontSize: "0.88rem", lineHeight: 1.6 }}>
                      {item.description}
                    </div>
                  ) : null}
                  <div
                    style={{
                      padding: mobile ? "0 0.9rem 0.9rem" : "0.75rem 0.9rem 0.75rem 0",
                      display: "flex",
                      justifyContent: mobile ? "flex-start" : "flex-end",
                      alignItems: mobile ? "flex-start" : "center",
                    }}
                  >
                    <Button
                      type="button"
                      label="Open"
                      severity="warning"
                      rounded
                      outlined
                      size="small"
                      onClick={item.action}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Dialog>
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          `${actorLabel} Dashboard`,
          `Manage your ${facilityPluralLabel.toLowerCase()}, residents, and care plans in one place.`,
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: mobile ? "center" : "flex-end",
              gap: "0.65rem",
              flexWrap: "wrap",
              width: mobile ? "100%" : "auto",
            }}
          >
            {(businessHomes?.length || 0) > 1 ? (
              <div className="flex align-items-center justify-content-center gap-2 flex-wrap">
                <span style={{ color: "#475569", fontWeight: 700, fontSize: "0.84rem", whiteSpace: "nowrap" }}>Select Home</span>
                <Dropdown value={selectedHomeId} options={businessHomes} optionLabel="businessName" optionValue="homeId" onChange={(event) => setSelectedHomeId(String(event.value || ""))} placeholder="Select home" aria-label="Select home" className="provider-home-dropdown" panelClassName="provider-home-dropdown-panel" style={{ width: mobile ? "min(15rem, 100%)" : "15rem" }} />
              </div>
            ) : null}
            <div style={{ display: "flex", justifyContent: "center", width: mobile ? "100%" : "auto" }}>
              <Button type="button" label={addFacilityLabel} severity="warning" rounded size="small" data-tour="provider-add-home" onClick={() => { if (isStepActive("add-home-cta")) completeStep("add-home-cta"); setOpenModal(true); }} />
            </div>
            {(businessHomes?.length || 0) > 0 && accessUnlockActive && accessUnlockDaysLeft > 0 ? (
              <div
                style={{
                  borderRadius: "999px",
                  border: "1px solid #93c5fd",
                  background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #dbeafe 100%)",
                  boxShadow: "0 10px 24px rgba(37, 99, 235, 0.1)",
                  padding: mobile ? "0.5rem 0.75rem" : "0.52rem 0.85rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.55rem",
                  flexWrap: "wrap",
                  textAlign: "center",
                  width: mobile ? "100%" : "auto",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "1.75rem",
                    height: "1.75rem",
                    borderRadius: "999px",
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    boxShadow: "inset 0 0 0 1px #93c5fd",
                    fontSize: "0.78rem",
                  }}
                  className="pi pi-bolt"
                />
                <div
                  style={{
                    fontSize: mobile ? "0.82rem" : "0.86rem",
                    fontWeight: 700,
                    color: "#1e3a8a",
                    lineHeight: 1.35,
                  }}
                >
                  Access Unlock active:
                  <span style={{ color: "#1d4ed8", marginLeft: "0.3rem" }}>
                    {accessUnlockDaysLeft} day{accessUnlockDaysLeft === 1 ? "" : "s"} left
                    {formattedAccessUnlockEndDate ? ` until ${formattedAccessUnlockEndDate}` : ""}
                  </span>
                </div>
              </div>
            ) : null}

          </div>,
        )}

        <div>
          {loading ? (
            <div className="card flex justify-content-center p-5" style={dashboardSectionCardStyle}>
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
            </div>
          ) : (businessHomes?.length === 0 ? null : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: "1rem",
                alignItems: "start",
              }}
            >
              <div
                style={{
                  ...dashboardSectionCardStyle,
                  padding: "0.7rem",
                  boxShadow: "none",
                  display: "none",
                }}
              >
                <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ margin: "0.2rem 0.25rem 0.75rem" }}>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>
                    {facilityPluralLabel}
                    <span style={{ color: "#64748b", fontWeight: 700, marginLeft: "0.45rem", fontSize: "0.9rem" }}>
                      {businessHomes?.length || 0}
                    </span>
                  </div>
                  <Button
                    type="button"
                    label={addFacilityLabel}
                    severity="warning"
                    rounded
                    outlined
                    data-tour="provider-add-home"
                    onClick={() => {
                      if (isStepActive("add-home-cta")) {
                        completeStep("add-home-cta");
                      }
                      setOpenModal(true);
                    }}
                  />
                </div>
                {businessHomes?.length === 0 ? (
                  <div style={{ color: "#64748b", fontStyle: "italic", padding: "0.25rem 0.35rem 0.5rem" }}>
                    {`No ${facilityPluralLabel.toLowerCase()} added yet.`}
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    <div className="flex flex-column gap-2">
                      {(businessHomes as BusinessHomeProfile[]).map((home) => {
                        const isSelected = home.homeId === selectedHomeId;
                        const residentCount = home.homeId
                          ? (residentsByHome[home.homeId] || []).filter((resident) => isActiveResident(resident)).length
                          : 0;
                        return (
                          <button
                            key={home.homeId || home.businessName}
                            type="button"
                            onClick={() => setSelectedHomeId(home.homeId || "")}
                            style={{
                              textAlign: "left",
                              width: "100%",
                              border: isSelected ? "1px solid #fdba74" : "1px solid #dbe7f3",
                              background: isSelected ? "#fff7ed" : "#ffffff",
                              borderRadius: "16px",
                              padding: "0.9rem 1rem",
                              cursor: "pointer",
                              boxShadow: isSelected ? "0 10px 24px rgba(249, 115, 22, 0.12)" : "none",
                            }}
                          >
                            <div className="flex justify-content-between align-items-start gap-2">
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
                                  {home.businessName}
                                </div>
                                {home.accessType === "shared_admin" ? (
                                  <div style={{ marginTop: "0.35rem" }}>
                                    <Tag value={home.accessLabel || "Shared Provider Access"} severity="info" />
                                  </div>
                                ) : null}
                                {home.formattedAddress ? (
                                  <div style={{ color: "#64748b", fontSize: "0.82rem", marginTop: "0.18rem", lineHeight: 1.45 }}>
                                    {home.formattedAddress}
                                  </div>
                                ) : null}
                              </div>
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "1.7rem",
                                  height: "1.7rem",
                                  borderRadius: "999px",
                                  background: residentCount > 0 ? "#eff6ff" : "#f1f5f9",
                                  border: residentCount > 0 ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                                  color: residentCount > 0 ? "#185DA2" : "#94a3b8",
                                  fontWeight: 700,
                                  fontSize: "0.82rem",
                                  padding: "0 0.45rem",
                                }}
                              >
                                {loadingResidents && home.homeId && !(home.homeId in residentsByHome) ? "..." : residentCount}
                              </div>
                            </div>
                            <div style={{ color: "#475569", fontSize: "0.8rem", marginTop: "0.42rem" }}>
                              Created {convertIsoToDate(home.createdAt)}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                  </div>
                )}
              </div>

              <section
                aria-label="Home details"
                data-home-id={selectedHome?.homeId || undefined}
                style={{
                  background: "#ffffff",
                  padding: mobile ? "0.9rem" : "1rem",
                }}
              >
                {selectedHome ? (
                  <>
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0f172a" }}>
                          {selectedHome.businessName}
                        </div>
                        {selectedHome.accessType === "shared_admin" ? (
                          <div style={{ marginTop: "0.45rem" }}>
                            <Tag value={selectedHome.accessLabel || "Shared Provider Access"} severity="info" />
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          label="Add Resident"
                          icon="pi pi-plus"
                          text
                          style={{ color: "#9a3412", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#fff7ed", border: "1px solid #fdba74", borderRadius: "999px" }}
                          data-tour={selectedHome.homeId === firstHomeId ? "provider-add-resident" : undefined}
                          disabled={selectedHomeActiveResidentLimit > 0 && activeSelectedHomeResidents.length >= selectedHomeActiveResidentLimit}
                          onClick={() => openAddResidentForHome(selectedHome.homeId)}
                        />
                        {activeSelectedHomeResidents.length > 0 ? (
                          <>
                            <Button
                              type="button"
                              label="Manage Staff"
                              text
                              icon="pi pi-id-card"
                              style={{ color: "#6d28d9", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "999px", justifyContent: "flex-start" }}
                              onClick={() => runSubscribedProviderAction("Manage Staff", () => navigate(RouterAddress.getHomeStaffManagementPagePath(String(selectedHome?.homeId || ""))))}
                            />
                          </>
                        ) : null}

                        <Button
                          type="button"
                          label="Home Binder"
                          text
                          icon="pi pi-book"
                          style={{ color: "#0f766e", fontWeight: 700, padding: "0.2rem 0.6rem", fontSize: "0.74rem", whiteSpace: "nowrap", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: "999px" }}
                          onClick={() => navigate(RouterAddress.getHomeBinderPagePath(selectedHome.homeId))}
                        />
                        <ActionMenuButton
                          label={manageFacilityLabel}
                          model={selectedHomeManageItems}
                          tiered
                          severity="warning"
                          size="small"
                          rounded
                          disabled={editHomeLoading}
                          dataTour={selectedHome.homeId === firstHomeId ? "provider-open-home" : undefined}
                          className="p-button-sm"
                          style={{ minWidth: "9.5rem" }}
                          menuStyle={{ minWidth: "13rem" }}
                        />
                      </div>
                    </div>
                    <>
                      <div id="provider-dashboard-residents" style={{ marginTop: "1rem", borderTop: "1px solid #e2e8f0", paddingTop: "1rem", scrollMarginTop: "1rem", display: displayedSelectedHomeResidents.length > 0 || archivedSelectedHomeResidents.length > 0 ? undefined : "none" }}>
                        <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.7rem" }}>
                          <div style={{ color: "#64748b", fontWeight: 700, fontSize: "0.88rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {showArchivedResidents
                              ? `Archived Residents (${archivedSelectedHomeResidents.length})`
                              : `Active Residents (${activeSelectedHomeResidents.length})`}
                          </div>
                          <div
                            className="flex align-items-center gap-2 flex-wrap"
                            style={{
                              width: mobile ? "100%" : "auto",
                              justifyContent: mobile ? "flex-start" : "flex-end",
                            }}
                          >
                            {isOneHubForCareBrand && displayedSelectedHomeResidents.length ? (
                              <>
                                <InputText
                                  value={adminResidentSearch}
                                  onChange={(event) => setAdminResidentSearch(event.target.value)}
                                  placeholder="Search resident name ..."
                                  style={{ width: mobile ? "100%" : "28rem" }}
                                />
                              </>
                            ) : null}
                            {showArchivedResidents || archivedSelectedHomeResidents.length > 0 ? (
                              <div
                                className="flex align-items-center gap-2"
                                style={{
                                  width: "auto",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <label htmlFor="archived-residents-toggle" className="font-medium">
                                  {showArchivedResidents
                                    ? `Show Active (${activeSelectedHomeResidents.length})`
                                    : `Show Archived (${archivedSelectedHomeResidents.length})`}
                                </label>
                                <InputSwitch
                                  inputId="archived-residents-toggle"
                                  checked={showArchivedResidents}
                                  onChange={(event) => setShowArchivedResidents(!!event.value)}
                                  aria-label="Show archived residents"
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                        {!showArchivedResidents ? (
                          <div style={{ color: "#64748b", fontSize: "0.86rem", marginBottom: "0.8rem" }}>
                            {isAdminUser
                              ? `Add a resident to this ${facilitySingularLabelLower} and keep their care tools organized in one place.`
                              : selectedHomeActiveResidentLimit > 0 && activeSelectedHomeResidents.length >= selectedHomeActiveResidentLimit
                                ? `This ${facilitySingularLabelLower} already has ${selectedHomeActiveResidentLimit} active residents. ${selectedHomeLimitLabel} can only have up to ${selectedHomeActiveResidentLimit} active residents at a time.`
                                : Number(providerAllowedResidents || 0) > 0
                                  ? `This ${facilitySingularLabelLower} has ${activeSelectedHomeResidents.length} active resident${activeSelectedHomeResidents.length === 1 ? "" : "s"}. ${totalActiveResidentCountAcrossHomes} of ${providerAllowedResidents} active resident slot${providerAllowedResidents === 1 ? "" : "s"} used across all ${facilityPluralLabel.toLowerCase()}.`
                                  : `Add a resident to this ${facilitySingularLabelLower} and keep their care tools organized in one place.`}
                          </div>
                        ) : null}
                        {loadingResidents && selectedHome.homeId && !(selectedHome.homeId in residentsByHome) ? (
                          <div style={{ color: "#64748b", fontStyle: "italic" }}>
                            Loading residents...
                          </div>
                        ) : residentTableResidents.length ? (
                          <>
                          <div style={{ borderTop: "1px solid #e2e8f0" }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.55fr) minmax(320px, 1.9fr) minmax(140px, 0.9fr)",
                                gap: "0.75rem",
                                padding: "0.8rem 1rem",
                                background: "#f8fafc",
                                borderBottom: "1px solid #e2e8f0",
                                color: "#64748b",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              <div>Resident</div>
                              {!mobile ? <div>Resident Tools</div> : null}
                              {!mobile ? <div style={{ textAlign: "right" }}>Daily Tracker</div> : null}
                            </div>
                            {residentTablePageResidents.map((resident, index) => (
                              renderResidentTableRow(resident, selectedHome.homeId || "", residentTableFirst + index)
                            ))}
                          </div>
                          {residentTableResidents.length > RESIDENT_TABLE_PAGE_SIZE ? (
                            <div className="mt-3 flex justify-content-end">
                              <Paginator
                                first={residentTableFirst}
                                rows={RESIDENT_TABLE_PAGE_SIZE}
                                totalRecords={residentTableResidents.length}
                                rowsPerPageOptions={[RESIDENT_TABLE_PAGE_SIZE]}
                                onPageChange={(event) => setResidentTableFirst(event.first)}
                              />
                            </div>
                          ) : null}
                          </>
                        ) : (
                          <div style={{ color: "#64748b", fontStyle: "italic" }}>
                            {showArchivedResidents
                              ? "No archived residents."
                              : "No active residents added yet. Use Add Resident to create the first one and start care planning."}
                          </div>
                        )}
                      </div>

                      {emarBoardRows.length ? (
                        <div id="provider-dashboard-emar-board" style={{ marginTop: "1rem", borderTop: "1px solid #e2e8f0", paddingTop: "1rem", scrollMarginTop: "1rem" }}>
                          <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.7rem" }}>
                            <div style={{ color: "#64748b", fontWeight: 700, fontSize: "0.88rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              eMAR Board ({emarBoardRows.length})
                            </div>
                            {isOneHubForCareBrand ? (
                              <div
                                className="flex align-items-center gap-2 flex-wrap"
                                style={{
                                  width: mobile ? "100%" : "auto",
                                  justifyContent: mobile ? "flex-start" : "flex-end",
                                }}
                              >
                                <InputText
                                  value={adminEmarBoardSearch}
                                  onChange={(event) => setAdminEmarBoardSearch(event.target.value)}
                                  placeholder="Search resident name ..."
                                  style={{ width: mobile ? "100%" : "24rem" }}
                                />
                              </div>
                            ) : null}
                          </div>
                          <div style={{ color: "#64748b", fontSize: "0.86rem", marginBottom: "0.8rem" }}>
                            See which residents are still pending, fully complete, or need medication follow-up today.
                          </div>
                          {emarBoardLoading ? (
                            <div style={{ color: "#64748b", fontStyle: "italic" }}>
                              Loading eMAR board...
                            </div>
                          ) : isOneHubForCareBrand ? (
                            <div style={{ display: "grid", gap: "0.85rem" }}>
                              <div className="flex gap-2 flex-wrap">
                                {[
                                  { key: "all", label: `All (${emarBoardSummary.total})` },
                                  { key: "attention", label: `Needs Attention (${emarBoardSummary.attention})` },
                                  { key: "due_now", label: `Due Now (${emarBoardSummary.dueNow})` },
                                  { key: "complete", label: `Complete (${emarBoardSummary.complete})` },
                                ].map((item) => (
                                  <Button
                                    key={item.key}
                                    type="button"
                                    label={item.label}
                                    severity={emarBoardFilter === item.key ? "warning" : "secondary"}
                                    rounded
                                    outlined={emarBoardFilter !== item.key}
                                    onClick={() => setEmarBoardFilter(item.key as "all" | "attention" | "due_now" | "complete" | "setup")}
                                  />
                                ))}
                              </div>
                              <div style={{ borderTop: "1px solid #e2e8f0" }}>
                                <DataTable
                                  value={adminEmarBoardTableRows}
                                  dataKey="residentId"
                                  paginator
                                  rows={10}
                                  rowsPerPageOptions={[10, 20, 50]}
                                  responsiveLayout="scroll"
                                  sortField={adminEmarBoardSortField}
                                  sortOrder={adminEmarBoardSortOrder}
                                  onSort={(event) => {
                                    setAdminEmarBoardSortField(String(event.sortField || "residentName"));
                                    setAdminEmarBoardSortOrder((event.sortOrder === -1 ? -1 : 1) as 1 | -1);
                                  }}
                                  emptyMessage="No eMAR board rows matched the current filters."
                                  className="p-datatable-sm p-datatable-striped"
                                >
                                  <Column field="residentName" header="Resident" sortable body={renderAdminEmarResidentCell} style={{ minWidth: "16rem" }} />
                                  <Column field="statusLabel" header="Status" sortable body={renderAdminEmarStatusCell} style={{ minWidth: "10rem" }} />
                                  <Column field="summaryDesktop" header="Today" body={renderAdminEmarTodayCell} style={{ minWidth: "24rem" }} />
                                  <Column field="pendingCount" header="Pending" sortable style={{ minWidth: "7rem" }} body={(row: any) => <span style={{ fontWeight: 700, color: "#0f172a" }}>{row.pendingCount}</span>} />
                                </DataTable>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ borderTop: "1px solid #e2e8f0" }}>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.2fr) minmax(140px, 0.7fr) minmax(320px, 1.5fr)",
                                    gap: "0.6rem",
                                    padding: "0.8rem 1rem",
                                    background: "#f8fafc",
                                    borderBottom: "1px solid #e2e8f0",
                                    color: "#64748b",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  <div>Resident</div>
                                  {!mobile ? <div>Status</div> : null}
                                  {!mobile ? <div>Today</div> : null}
                                </div>
                                {emarBoardRows.map(({ resident, residentId, emarStatus }) => {
                                  const firstName = String(resident?.firstName || "").trim();
                                  const lastName = String(resident?.lastName || "").trim();
                                  const residentName = `${firstName} ${lastName}`.trim() || "Resident";
                                  return (
                                    <div
                                      key={`emar-board-${residentId}`}
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.2fr) minmax(140px, 0.7fr) minmax(320px, 1.5fr)",
                                        gap: "0.6rem",
                                        padding: "0.9rem 1rem",
                                        borderBottom: "1px solid #eef2f7",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ minWidth: 0 }}>
                                        <div
                                          style={{
                                            fontWeight: 800,
                                            color: "#0f172a",
                                            lineHeight: 1.35,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                          title={residentName}
                                        >
                                          {residentName}
                                        </div>
                                        {mobile ? (
                                          <div className="mt-2 flex gap-2 flex-wrap">
                                            <Tag value={emarStatus?.statusLabel || "Pending"} severity={emarStatus?.severity || "warning"} rounded />
                                          </div>
                                        ) : null}
                                      </div>
                                      {!mobile ? (
                                        <div>
                                          <Tag value={emarStatus?.statusLabel || "Pending"} severity={emarStatus?.severity || "warning"} rounded />
                                        </div>
                                      ) : null}
                                      {!mobile ? (
                                        <div style={{ color: "#475569", fontSize: "0.85rem", lineHeight: 1.35, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {emarStatus?.summaryDesktop || "No active meds"}
                                        </div>
                                      ) : (
                                        <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.5 }}>
                                          {emarStatus?.summaryMobile || "No active meds set up yet."}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      ) : null}

                      <div id="provider-dashboard-caregivers" style={{ marginTop: "1rem", borderTop: "1px solid #e2e8f0", paddingTop: "1rem", scrollMarginTop: "1rem", display: selectedHomeCaregiverRows.length > 0 ? undefined : "none" }}>
                        <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.7rem" }}>
                          <div style={{ color: "#64748b", fontWeight: 700, fontSize: "0.88rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Staff ({selectedHomeCaregiverRows.length})
                          </div>
                        </div>

                        {loadingCaregivers ? (
                          <div style={{ color: "#64748b", fontStyle: "italic", marginTop: "0.9rem" }}>
                            Loading staff...
                          </div>
                        ) : selectedHomeCaregiverRows.length ? (
                          <div style={{ borderTop: "1px solid #e2e8f0" }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1fr) minmax(150px, 0.8fr) minmax(260px, 1.4fr)",
                                gap: "0.75rem",
                                padding: "0.8rem 1rem",
                                background: "#f8fafc",
                                borderBottom: "1px solid #e2e8f0",
                                color: "#64748b",
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              <div>Staff</div>
                              {!mobile ? <div>Role</div> : null}
                              {!mobile ? <div>Shift</div> : null}
                            </div>
                            {selectedHomeCaregiverRows.map((caregiverRow) => (
                              <div
                                key={caregiverRow.caregiverId}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1fr) minmax(150px, 0.8fr) minmax(260px, 1.4fr)",
                                  gap: "0.75rem",
                                  padding: "0.85rem 1rem",
                                  borderBottom: "1px solid #eef2f7",
                                  alignItems: "center",
                                }}
                              >
                                <div style={{ color: "#0f172a", fontWeight: 700 }}>{caregiverRow.caregiverName}</div>
                                <div style={{ color: "#475569", fontSize: "0.88rem" }}>
                                  {caregiverRow.userRoleType
                                    ? String(caregiverRow.userRoleType).replace(/_/g, " ")
                                    : "Staff"}
                                </div>
                                <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.45 }}>{caregiverRow.availabilitySummary}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        <div style={{ display: "none" }}>
                        {loadingCaregivers ? (
                          <div style={{ color: "#64748b", fontStyle: "italic", marginTop: "0.9rem" }}>
                            Loading staff...
                          </div>
                        ) : selectedHomeCaregiverRows.length ? (
                          <>
                            <div style={{ marginTop: "1rem", paddingTop: "0.25rem" }}>
                              <div className="flex justify-content-between align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                                <div style={{ color: "#64748b", fontSize: "0.86rem", lineHeight: 1.5 }}>
                                  Review one staff member at a time so resident access stays easy to audit.
                                </div>
                                {selectedPermissionsCaregiverRow && activeSelectedHomeResidents.length > 0 ? (
                                  <div className="flex gap-2 flex-wrap">
                                    <Button
                                      type="button"
                                      label="Manage Staff"
                                      severity="warning"
                                      rounded
                                      outlined
                                      onClick={() => navigate(RouterAddress.getHomeStaffManagementPagePath(String(selectedHome?.homeId || "")))}
                                    />
                                  </div>
                                ) : null}
                              </div>

                              <div className="flex flex-wrap gap-2" style={{ marginBottom: "0.9rem" }}>
                                {selectedHomeCaregiverRows.map((caregiverRow) => (
                                  <Button
                                    key={caregiverRow.caregiverId}
                                    type="button"
                                    label={caregiverRow.caregiverName}
                                    rounded
                                    outlined={selectedPermissionsCaregiverRow?.caregiverId !== caregiverRow.caregiverId}
                                    severity={selectedPermissionsCaregiverRow?.caregiverId === caregiverRow.caregiverId ? "warning" : "secondary"}
                                    onClick={() => setSelectedCaregiverForPermissions(caregiverRow.caregiverId)}
                                  />
                                ))}
                              </div>

                              {selectedPermissionsCaregiverRow ? (
                                <div
                                  style={{
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                    background: "#ffffff",
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "0.9rem 1rem",
                                      background: "#f8fafc",
                                      borderBottom: "1px solid #e2e8f0",
                                      display: "grid",
                                      gap: "0.55rem",
                                    }}
                                  >
                                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                      <div>
                                        <div style={{ color: "#0f172a", fontWeight: 800, fontSize: "0.96rem" }}>
                                          {selectedPermissionsCaregiverRow.caregiverName}
                                        </div>
                                      </div>
                                      <div className="flex gap-2 flex-wrap align-items-center">
                                        <Tag
                                          value={selectedPermissionsCaregiverRow.shiftEnabled ? "Shift set" : "No shift set"}
                                          severity={selectedPermissionsCaregiverRow.shiftEnabled ? "success" : "warning"}
                                          rounded
                                        />
                                        <Tag
                                          value={selectedPermissionsCaregiverRow.mobileAccessEnabled ? "Mobile access on" : "Mobile access off"}
                                          severity={selectedPermissionsCaregiverRow.mobileAccessEnabled ? "info" : "secondary"}
                                          rounded
                                        />
                                        <Tag
                                          value={`${selectedPermissionsCaregiverRow.sharedCount} resident${selectedPermissionsCaregiverRow.sharedCount === 1 ? "" : "s"}`}
                                          severity="warning"
                                          rounded
                                        />
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr",
                                        gap: "0.65rem",
                                      }}
                                    >
                                      <div
                                        style={{
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "12px",
                                          background: "#ffffff",
                                          padding: "0.7rem 0.8rem",
                                        }}
                                      >
                                        <div style={{ color: "#64748b", fontSize: "0.76rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                          Availability
                                        </div>
                                        <div style={{ color: "#0f172a", fontWeight: 700, lineHeight: 1.45, marginTop: "0.2rem" }}>
                                          {selectedPermissionsCaregiverRow.availabilitySummary}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {mobile ? (
                                    <div style={{ display: "grid", gap: "0.75rem", padding: "0.9rem 1rem" }}>
                                      {activeSelectedHomeResidents.map((resident) => {
                                        const residentId = String(resident?.residentId || "").trim();
                                        const residentShare = selectedPermissionsCaregiverRow.sharedResidents.find((item: any) => item.residentId === residentId);
                                        return (
                                          <div
                                            key={residentId}
                                            style={{
                                              border: "1px solid #e2e8f0",
                                              borderRadius: "12px",
                                              background: "#f8fafc",
                                              padding: "0.8rem 0.9rem",
                                              display: "grid",
                                              gap: "0.45rem",
                                            }}
                                          >
                                            <div style={{ color: "#0f172a", fontWeight: 700 }}>
                                              {`${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident"}
                                            </div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>Notes: {residentShare?.dailyNotesAccessEnabled ? "Yes" : "No"}</div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>Incidents: {residentShare?.incidentReportsAccessEnabled ? "Yes" : "No"}</div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>Behavior Tracker: {residentShare?.behaviorLogAccessEnabled ? "Yes" : "No"}</div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>Care Plan: {residentShare?.carePlanAccessEnabled ? "Yes" : "No"}</div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>eMAR: {residentShare?.emarAccessEnabled ? "Yes" : "No"}</div>
                                            <div style={{ color: "#475569", fontSize: "0.88rem" }}>ADL: {residentShare?.adlAccessEnabled ? "Yes" : "No"}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <>
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns: mobile ? "minmax(0, 1.4fr) repeat(6, minmax(72px, 0.7fr))" : "minmax(220px, 1.6fr) repeat(6, minmax(96px, 0.8fr))",
                                          gap: "0.5rem",
                                          padding: "0.8rem 1rem",
                                          background: "#f8fafc",
                                          borderBottom: "1px solid #e2e8f0",
                                          color: "#64748b",
                                          fontWeight: 700,
                                          fontSize: "0.78rem",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.05em",
                                        }}
                                      >
                                        <div>Resident</div>
                                        <div style={{ textAlign: "center" }}>Notes</div>
                                        <div style={{ textAlign: "center" }}>Incidents</div>
                                        <div style={{ textAlign: "center" }}>Behavior</div>
                                        <div style={{ textAlign: "center" }}>Care Plan</div>
                                        <div style={{ textAlign: "center" }}>eMAR</div>
                                        <div style={{ textAlign: "center" }}>ADL</div>
                                      </div>
                                      {activeSelectedHomeResidents.map((resident) => {
                                        const residentId = String(resident?.residentId || "").trim();
                                        const residentShare = selectedPermissionsCaregiverRow.sharedResidents.find((item: any) => item.residentId === residentId);
                                        return (
                                          <div
                                            key={residentId}
                                            style={{
                                              display: "grid",
                                              gridTemplateColumns: mobile ? "minmax(0, 1.4fr) repeat(6, minmax(72px, 0.7fr))" : "minmax(220px, 1.6fr) repeat(6, minmax(96px, 0.8fr))",
                                              gap: "0.5rem",
                                              padding: "0.85rem 1rem",
                                              borderBottom: "1px solid #eef2f7",
                                              alignItems: "center",
                                            }}
                                          >
                                            <div style={{ color: "#0f172a", fontWeight: 700, minWidth: 0 }}>
                                              {`${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident"}
                                            </div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.dailyNotesAccessEnabled ? "✅" : "—"}</div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.incidentReportsAccessEnabled ? "✅" : "—"}</div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.behaviorLogAccessEnabled ? "✅" : "—"}</div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.carePlanAccessEnabled ? "✅" : "—"}</div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.emarAccessEnabled ? "✅" : "—"}</div>
                                            <div style={{ textAlign: "center" }}>{residentShare?.adlAccessEnabled ? "✅" : "—"}</div>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                                  Select a caregiver to review resident permissions.
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div style={{ color: "#64748b", fontStyle: "italic", marginTop: "0.9rem" }}>
                            {`No caregivers linked to this ${facilitySingularLabelLower} yet.`}
                          </div>
                        )}
                        </div>
                      </div>
                    </>
                  </>
                ) : (
                  <div style={{ color: "#64748b", fontStyle: "italic" }}>
                    {`Select a ${facilitySingularLabelLower} to manage residents and ${facilitySingularLabelLower} tools, or add your first ${facilitySingularLabelLower} to get started.`}
                  </div>
                )}
              </section>
            </div>
          ))}
        </div>

      </div>

      <AddHomeModal isOpen={openModal} toggleModal={setOpenModal} addFlag={addFlag} setAddFlag={handleHomeAddFlagChange} />
      <AddResidentModal
        homeId={selectedHomeIdForResident}
        facilityType={(businessHomes as BusinessHomeProfile[]).find((home) => home.homeId === selectedHomeIdForResident)?.facilityType}
        isOpen={openResidentModal}
        toggleModal={setOpenResidentModal}
        flag={addFlag}
        setFlag={setAddFlag}
        onAdded={async (resident) => {
          const residentHomeId = String(resident?.homeId || selectedHomeIdForResident || "").trim();
          if (!residentHomeId) {
            return;
          }
          await refreshResidentsForHome(residentHomeId);
        }}
      />
      <EditHomeProfileModal
        isOpen={openEditHomeModal}
        toggleModal={(open) => {
          setOpenEditHomeModal(open);
          if (!open) {
            setEditHomeData(null);
          }
        }}
        addFlag={addFlag}
        setAddFlag={setAddFlag}
        homeDetails={editHomeData}
        showArchiveAction={isProviderSubscribed}
        onArchive={() => {
          setOpenEditHomeModal(false);
          deleteSelectedHome(String(editHomeData?.homeId || "").trim());
        }}
      />
      <Dialog
        visible={shareDialogVisible}
        onHide={() => {
          if (!savingCaregiverShares) {
            setShareDialogVisible(false);
            setSelectedCaregiverForSharing(null);
          }
        }}
        header="Share Residents"
        style={{ width: mobile ? "96vw" : "42rem" }}
        footer={(
          <div className="flex justify-content-between align-items-end gap-3 flex-wrap">
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#f8fafc",
                padding: "0.65rem 0.8rem",
                minWidth: mobile ? "100%" : "16rem",
                textAlign: "left",
                alignSelf: "stretch",
              }}
            >
              <div style={{ fontWeight: 800, color: "#334155", fontSize: "0.82rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Sharing Options
              </div>
              <div style={{ marginTop: "0.45rem", color: "#64748b", lineHeight: 1.55, fontSize: "0.88rem" }}>
                Review one guide for Resident Share, Search Care Plan, Daily Behavior Log, Incident Reports, Progress Notes, and eMAR.
              </div>
              <div style={{ marginTop: "0.7rem" }}>
                <Button
                  type="button"
                  label="View Sharing Guide"
                  icon="pi pi-question-circle"
                  rounded
                  outlined
                  severity="secondary"
                  onClick={() => setCarePlanAccessHintVisible(true)}
                />
              </div>
            </div>
            <div className="flex justify-content-end gap-2 flex-wrap">
              <Button
                type="button"
                label="Cancel"
                outlined
                rounded
                severity="secondary"
                disabled={savingCaregiverShares}
                onClick={() => {
                  setShareDialogVisible(false);
                  setSelectedCaregiverForSharing(null);
                }}
              />
              <Button
                type="button"
                label="Save Access"
                rounded
                severity="warning"
                loading={savingCaregiverShares}
                onClick={onSaveCaregiverShares}
              />
            </div>
          </div>
        )}
      >
        <div style={{ marginTop: "1rem", color: "#64748b", lineHeight: 1.6 }}>
          {`Select which Residents this staff member can access from this ${facilitySingularLabelLower}. All operational tools are available for an assigned resident.`}
        </div>
        <div style={{ marginTop: "1rem", display: "grid", gap: "0.75rem" }}>
          {activeSelectedHomeResidents.length ? activeSelectedHomeResidents.map((resident) => {
            const residentId = String(resident?.residentId || "").trim();
            const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident";
            const checked = selectedSharedResidentIds.includes(residentId);
            return (
              <label
                key={residentId}
                htmlFor={`caregiver-share-${residentId}`}
                style={{
                  border: checked ? "1px solid #fdba74" : "1px solid #dbe7f3",
                  background: checked ? "#fff7ed" : "#ffffff",
                  borderRadius: "14px",
                  padding: "0.85rem 0.95rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.85rem",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  inputId={`caregiver-share-${residentId}`}
                  checked={checked}
                  onChange={(e) => setSelectedSharedResidentIds((prev) =>
                    e.checked
                      ? [...prev, residentId]
                      : prev.filter((item) => item !== residentId),
                  )}
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>{residentName}</span>
              </label>
            );
          }) : (
            <div style={{ color: "#64748b", fontStyle: "italic" }}>
              Add at least one active resident before sharing staff access.
            </div>
          )}
        </div>
      </Dialog>
      <Dialog
        visible={carePlanAccessHintVisible}
        onHide={() => setCarePlanAccessHintVisible(false)}
        header="Sharing Guide"
        style={{ width: mobile ? "96vw" : "42rem" }}
        footer={(
          <div className="flex justify-content-end">
            <Button
              type="button"
              label="Close"
              rounded
              severity="warning"
              onClick={() => setCarePlanAccessHintVisible(false)}
            />
          </div>
        )}
      >
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "14px",
            overflow: "hidden",
            background: "#ffffff",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "minmax(150px, 0.9fr) minmax(0, 2fr)",
              gap: "0.75rem",
              padding: "0.8rem 0.95rem",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              color: "#64748b",
              fontWeight: 700,
              fontSize: "0.78rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            <div>Option</div>
            {!mobile ? <div>What It Allows</div> : null}
          </div>
          {allShareOptionHints.map((section, index) => (
            <div
              key={section.title}
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(150px, 0.9fr) minmax(0, 2fr)",
                gap: "0.75rem",
                padding: "0.85rem 0.95rem",
                borderBottom: index === allShareOptionHints.length - 1 ? "none" : "1px solid #eef2f7",
                alignItems: "start",
              }}
            >
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                {section.title}
              </div>
              <div style={{ display: "grid", gap: "0.28rem", color: "#475569", lineHeight: 1.55 }}>
                {mobile ? (
                  <div style={{ color: "#64748b", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    What It Allows
                  </div>
                ) : null}
                {section.details.map((detail) => (
                  <div key={`${section.title}-${detail}`}>{detail}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Dialog>
      <Dialog
        visible={shiftDialogVisible}
        onHide={() => {
          if (!savingCaregiverShift) {
            setShiftDialogVisible(false);
            setSelectedCaregiverForShift(null);
          }
        }}
        header="Set Staff Shift"
        style={{ width: mobile ? "96vw" : "34rem" }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              outlined
              rounded
              severity="secondary"
              disabled={savingCaregiverShift}
              onClick={() => {
                setShiftDialogVisible(false);
                setSelectedCaregiverForShift(null);
              }}
            />
            <Button
              type="button"
              label="Save Shift"
              rounded
              severity="warning"
              loading={savingCaregiverShift}
              onClick={onSaveCaregiverShift}
            />
          </div>
        )}
      >
        <div style={{ color: "#64748b", lineHeight: 1.6 }}>
          Choose when{" "}
          <strong>{`${selectedCaregiverForShift?.firstName || ""} ${selectedCaregiverForShift?.lastName || ""}`.trim() || selectedCaregiverForShift?.email || "this staff member"}</strong>{" "}
          can access shared resident data for <strong>{selectedHome?.businessName || `this ${facilitySingularLabelLower}`}</strong>.
        </div>
        <div style={{ marginTop: "1rem", display: "grid", gap: "1rem" }}>
          <div
            style={{
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              borderRadius: 14,
              padding: "0.9rem 1rem",
              display: "grid",
              gap: "0.55rem",
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
              <Checkbox
                inputId="caregiver-mobile-access-enabled"
                checked={mobileAccessEnabled}
                onChange={(e) => setMobileAccessEnabled(Boolean(e.checked))}
              />
              <span style={{ color: "#0f172a", fontWeight: 700 }}>Allow mobile access</span>
            </label>
            <div style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.55, paddingLeft: "2rem" }}>
              When this is on, staff can use use their mobile phones. Turn it off if this caregiver should only access shared resident data from desktop or laptop.
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }}>
            <Checkbox
              inputId="caregiver-shift-enabled"
              checked={shiftEnabled}
              onChange={(e) => {
                const checked = Boolean(e.checked);
                setShiftEnabled(checked);
                if (!checked) {
                  setAllDayShiftAccess(false);
                }
              }}
            />
            <span style={{ color: "#0f172a", fontWeight: 700 }}>Enable scheduled access</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: shiftEnabled ? "pointer" : "not-allowed", opacity: shiftEnabled ? 1 : 0.55 }}>
            <Checkbox
              inputId="caregiver-shift-all-day"
              checked={allDayShiftAccess}
              disabled={!shiftEnabled}
              onChange={(e) => onSelectAllDayShiftAccess(Boolean(e.checked))}
            />
            <span style={{ color: "#0f172a", fontWeight: 700 }}>All day access</span>
          </label>
          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>Days</div>
            <div className="flex flex-wrap gap-2">
              {shiftDayOptions.map((dayOption) => {
                const checked = shiftDays.includes(dayOption.key);
                return (
                  <label
                    key={dayOption.key}
                    htmlFor={`shift-day-${dayOption.key}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.55rem",
                      border: checked ? "1px solid #fdba74" : "1px solid #dbe7f3",
                      background: checked ? "#fff7ed" : "#ffffff",
                      borderRadius: "999px",
                      padding: "0.55rem 0.8rem",
                      cursor: shiftEnabled ? "pointer" : "not-allowed",
                      opacity: shiftEnabled ? 1 : 0.55,
                    }}
                  >
                    <Checkbox
                      inputId={`shift-day-${dayOption.key}`}
                      checked={checked}
                      disabled={!shiftEnabled}
                      onChange={(e) => {
                        setShiftDays((prev) =>
                          e.checked
                            ? [...prev, dayOption.key]
                            : prev.filter((value) => value !== dayOption.key),
                        );
                      }}
                    />
                    <span style={{ fontWeight: 700, color: "#0f172a" }}>{dayOption.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>Start Time</div>
              <select
                value={shiftStartTime}
                disabled={!shiftEnabled || allDayShiftAccess}
                onChange={(event) => setShiftStartTime(event.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  borderRadius: 12,
                  padding: "0.82rem 0.9rem",
                  opacity: shiftEnabled && !allDayShiftAccess ? 1 : 0.55,
                  background: "#ffffff",
                  minHeight: 50,
                }}
              >
                {halfHourTimeOptions.map((timeOption) => (
                  <option key={timeOption.value} value={timeOption.value}>
                    {timeOption.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.55rem" }}>End Time</div>
              <select
                value={shiftEndTime}
                disabled={!shiftEnabled || allDayShiftAccess}
                onChange={(event) => setShiftEndTime(event.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  borderRadius: 12,
                  padding: "0.82rem 0.9rem",
                  opacity: shiftEnabled && !allDayShiftAccess ? 1 : 0.55,
                  background: "#ffffff",
                  minHeight: 50,
                }}
              >
                {halfHourTimeOptions.map((timeOption) => (
                  <option key={timeOption.value} value={timeOption.value}>
                    {timeOption.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ color: "#64748b", fontSize: "0.9rem" }}>
            Staff can open shared resident data only during the scheduled days and times.
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DashboardProvider;

